import { BLOCK_TYPES, getBlockColor, isBlockSolid, isBlockRaised } from './BlockTypes.js'
import { Zone } from './Zone.js'
import { DungeonGrid } from './DungeonGrid.js'
import { QUEST_BRANCHES } from './QuestBranches.js'
import {
  GRAVESTONE_SPRITE,
  KEY_SPRITE,
  LARGE_TREE_SPRITE,
  MAP_SCROLL_SPRITE,
  SWORD_SPRITE,
} from '../rendering/Sprites.js'
import { VILLAGER_SPRITE_SETS } from '../rendering/CharacterSpriteSheets.js'

const QUEST_ENCOUNTER_OFFSETS = [
  { x: 0, y: -1.5 },
  { x: -1.5, y: 0.75 },
  { x: 1.5, y: 0.75 },
  { x: 0, y: 1.75 }
]

export class World {
  constructor(game, saveData = null) {
    this.game = game
    this.tiles = new Map()
    this.modifiedTiles = new Map()
    this.protectedTiles = new Set()
    this.decorations = []
    this.bushes = []
    this.zones = []
    this.grids = [] // DungeonGrid per phase
    this.questBranches = []
    this.keyChests = []
    this.areaMaps = new Map()
    this.revealedTiles = new Set()
    this.exitLockNoticeAreaId = null
    this.fireAnimationTime = 0

    const cellSize = 9

    // Phase 1: 3 cols x 6 rows
    const forestExitCol = 1
    const graveyardEnterCol = 2

    const g1 = new DungeonGrid(3, 6, cellSize, 0, 0, 'forest')
    this.grids.push(g1)

    // Phase 2: 5 cols x 6 rows, directly below phase 1.
    // Align the forest exit and graveyard entrance.
    const p2OriginX = g1.originX + forestExitCol * cellSize - graveyardEnterCol * cellSize
    const p2OriginY = g1.originY + g1.rows * cellSize // flush against g1's bottom wall
    const g2 = new DungeonGrid(5, 6, cellSize, p2OriginX, p2OriginY, 'graveyard')
    this.grids.push(g2)

    // Phase 3: 5 cols x 7 rows, directly below the graveyard.
    const graveyardExitCol = 2
    const cavesEnterCol = 2
    const p3OriginX = g2.originX + graveyardExitCol * cellSize - cavesEnterCol * cellSize
    const p3OriginY = g2.originY + g2.rows * cellSize
    const g3 = new DungeonGrid(5, 7, cellSize, p3OriginX, p3OriginY, 'caves')
    this.grids.push(g3)

    // Phase 4: Ember Keep, a ruined fortress below the caves.
    const cavesExitCol = 2
    const emberKeepEnterCol = 2
    const p4OriginX = g3.originX + cavesExitCol * cellSize - emberKeepEnterCol * cellSize
    const p4OriginY = g3.originY + g3.rows * cellSize
    const g4 = new DungeonGrid(
      5,
      6,
      cellSize,
      p4OriginX,
      p4OriginY,
      'fortress',
      'emberkeep'
    )
    this.grids.push(g4)

    // Phase 5: Purple Dooms, the Dark King's final domain.
    const emberKeepExitCol = 2
    const purpleDoomsEnterCol = 2
    const p5OriginX = g4.originX + emberKeepExitCol * cellSize - purpleDoomsEnterCol * cellSize
    const p5OriginY = g4.originY + g4.rows * cellSize
    const g5 = new DungeonGrid(
      5,
      6,
      cellSize,
      p5OriginX,
      p5OriginY,
      'shadow',
      'purpledooms'
    )
    this.grids.push(g5)

    // Create zones from grid bounds.
    const b1 = g1.getBounds()
    this.zones.push(new Zone({
      name: 'forest',
      displayName: 'Peaceful Forest',
      bounds: b1,
      spawnPoint: g1.getCellCenter(2, 0),
      terrainType: 'forest',
      enemyTypes: ['zombie'],
      enemyCount: 6,
      chestCount: 2
    }))

    const b2 = g2.getBounds()
    this.zones.push(new Zone({
      name: 'graveyard',
      displayName: 'Graveyard',
      bounds: b2,
      spawnPoint: g2.getCellCenter(Math.floor(g2.cols / 2), 0),
      terrainType: 'graveyard',
      enemyTypes: ['skeleton', 'zombie', 'darkWizard'],
      enemyCount: 8,
      chestCount: 2
    }))

    const b3 = g3.getBounds()
    this.zones.push(new Zone({
      name: 'caves',
      displayName: 'Crystal Caves',
      bounds: b3,
      spawnPoint: g3.getCellCenter(cavesEnterCol, 0),
      terrainType: 'caves',
      enemyTypes: ['spider', 'orc'],
      enemyCount: 9,
      chestCount: 3
    }))

    const b4 = g4.getBounds()
    this.zones.push(new Zone({
      name: 'emberkeep',
      displayName: 'Ember Keep',
      bounds: b4,
      spawnPoint: g4.getCellCenter(emberKeepEnterCol, 0),
      terrainType: 'fortress',
      enemyTypes: ['emberGuard', 'cinderImp'],
      enemyCount: 10,
      chestCount: 3
    }))

    const b5 = g5.getBounds()
    this.zones.push(new Zone({
      name: 'purpledooms',
      displayName: 'Purple Dooms',
      bounds: b5,
      spawnPoint: g5.getCellCenter(purpleDoomsEnterCol, 0),
      terrainType: 'shadow',
      enemyTypes: ['shadowWolf', 'darkKnight'],
      enemyCount: 8,
      chestCount: 2,
      isBossZone: true
    }))

    this.buildPhase1(g1)
    this.buildPhase2(g2)
    this.buildPhase3(g3)
    this.buildPhase4(g4)
    this.buildPhase5(g5)
    this.buildKeyChests()
    this.buildQuestBranches()
    this.buildPhaseTransition(g1, g2, forestExitCol, graveyardEnterCol)
    this.buildPhaseTransition(g2, g3, graveyardExitCol, cavesEnterCol)
    this.buildPhaseTransition(g3, g4, cavesExitCol, emberKeepEnterCol)
    this.buildPhaseTransition(g4, g5, emberKeepExitCol, purpleDoomsEnterCol)
    this.stampAllGrids()
    this.stampHomeInterior(g1)
    this.decorateCrystalCaves(g3)
    this.decorateEmberKeep(g4)
    this.decoratePurpleDooms(g5)
    this.buildAreaBushes()

    // Place sword pickup in cell (1,0) of phase 1 (room left of spawn)
    const swordPos = g1.getCellCenter(1, 0)
    this.swordPickup = { x: swordPos.x, y: swordPos.y, collected: false }

    const forestMapPos = g1.getCellCenter(2, 2)
    const graveyardMapPos = g2.getCellCenter(0, 1)
    const cavesMapPos = g3.getCellCenter(4, 1)
    const emberKeepMapPos = g4.getCellCenter(4, 1)
    const purpleDoomsMapPos = g5.getCellCenter(4, 1)
    this.mapPickups = [
      { areaId: g1.areaId, x: forestMapPos.x, y: forestMapPos.y },
      { areaId: g2.areaId, x: graveyardMapPos.x, y: graveyardMapPos.y },
      { areaId: g3.areaId, x: cavesMapPos.x, y: cavesMapPos.y },
      { areaId: g4.areaId, x: emberKeepMapPos.x, y: emberKeepMapPos.y },
      { areaId: g5.areaId, x: purpleDoomsMapPos.x, y: purpleDoomsMapPos.y }
    ]

    const bossPosition = g5.getCellCenter(2, 5)
    this.bossSpawn = {
      id: 'purple_dooms_dark_king',
      type: 'darkKing',
      x: bossPosition.x,
      y: bossPosition.y
    }
    this.darkKingCheckpoint = g5.getCellCenter(2, 4)
    this.darkKingRetryHealth = null

    // One reusable upgrade shop in each level's map room.
    this.shopSpawns = this.mapPickups.map(pickup => ({
      id: `${pickup.areaId}_upgrade_shop`,
      areaId: pickup.areaId,
      x: pickup.x + 2,
      y: pickup.y,
      unattended: false
    }))

    const homeInterior = g1.getCellCenter(0, 0)
    const homeRoomCenter = g1.getCellCenter(2, 0)
    this.home = {
      interiorCell: { gridIndex: 0, col: 0, row: 0 },
      exteriorDoor: {
        x: homeRoomCenter.x,
        y: homeRoomCenter.y + 0.8
      },
      exteriorReturn: {
        x: homeRoomCenter.x,
        y: homeRoomCenter.y + 2
      },
      interiorSpawn: { x: homeInterior.x + 2, y: homeInterior.y + 1.4 },
      exitDoor: { x: homeInterior.x, y: homeInterior.y - 3.15 },
      key: { x: homeInterior.x + 2.6, y: homeInterior.y - 0.2 },
      couch: { x: homeInterior.x, y: homeInterior.y + 0.3 },
      bed: { x: homeInterior.x - 2.15, y: homeInterior.y + 3.1 },
      table: { x: homeInterior.x + 2, y: homeInterior.y + 3.18 },
      rug: { x: homeInterior.x, y: homeInterior.y + 3.55 },
      hasKey: false,
      doorUnlocked: true,
      sleepTimer: null,
      sleepDuration: 10
    }
    // Forest restarts land safely one block in front of the centered house.
    this.zones[0].spawnPoint = { ...this.home.exteriorReturn }

    this.buildPostgameVillage()
    this.shopSpawns.push(...this.postgameVillage.shops)

    this.enemySpawnBlockedAreas = [
      this.getCellBounds(g1, 0, 0),
      this.getCellBounds(g1, 1, 0),
      this.getCellBounds(g1, 2, 0),
      this.getCellBounds(g5, 2, 5),
      ...this.shopSpawns.map(shop => {
        const cell = this.getGridCellForPosition(shop.x, shop.y)
        return cell
          ? this.getCellBounds(this.grids[cell.gridIndex], cell.col, cell.row)
          : {
              minX: shop.x - 2,
              maxX: shop.x + 2,
              minY: shop.y - 2,
              maxY: shop.y + 2
            }
      }),
      ...this.questBranches.flatMap(branch => {
        const grid = this.grids[branch.gridIndex]
        return branch.cells.map(cell => this.getCellBounds(grid, cell.col, cell.row))
      })
    ]

    this.resetAreaMaps()

    if (saveData) {
      this.deserialize(saveData)
    }
  }

  buildPostgameVillage() {
    const pathStartX = Math.floor(this.grids[0].getBounds().maxX)
    const pathEndX = pathStartX + 20
    const pathY = Math.floor(this.home.exteriorReturn.y)
    const bounds = {
      minX: pathEndX + 1,
      maxX: pathEndX + 55,
      minY: pathY - 11,
      maxY: pathY + 12
    }

    const houseDoors = [
      [bounds.minX + 5, pathY - 5],
      [bounds.minX + 13, pathY - 5],
      [bounds.minX + 29, pathY - 5],
      [bounds.minX + 7, pathY + 9],
      [bounds.minX + 29, pathY + 9]
    ]
    const houseColors = ['#b95b3e', '#587cb8', '#8b67a9', '#c47b3c', '#4f8b68']
    const furnitureLayouts = [
      { couch: [-2.2, 3.2], bed: [2.2, 3.2], table: [2.1, 6.2], rug: [-0.3, 6.2] },
      { couch: [1.9, 3.3], bed: [-2.3, 6.1], table: [-2.1, 3.2], rug: [1.1, 6.2] },
      { couch: [-1.8, 6.1], bed: [2.3, 3.1], table: [-2.2, 3.1], rug: [1.1, 6.1] },
      { couch: [2, 6], bed: [-2.3, 3.2], table: [2.2, 3.2], rug: [-0.8, 6.1] },
      { couch: [-2, 3.2], bed: [-2.4, 6.2], table: [2.3, 6.1], rug: [0, 6.1] }
    ]
    const interiorStartX = bounds.maxX + 16
    const houses = houseDoors.map(([x, y], index) => {
      const minX = interiorStartX + index * 12
      const minY = bounds.minY
      return {
        id: `village_house_${index + 1}`,
        x,
        y,
        width: 4,
        height: 2.85,
        color: houseColors[index],
        furniture: furnitureLayouts[index],
        exteriorDoor: { x, y: y - 0.75 },
        exteriorReturn: { x, y: y + 0.65 },
        interiorBounds: { minX, maxX: minX + 9, minY, maxY: minY + 9 },
        interiorSpawn: { x: minX + 4.5, y: minY + 7.2 },
        exitDoor: { x: minX + 4.5, y: minY + 0.85 }
      }
    })
    const playerHome = {
      id: 'player_village_home',
      x: bounds.minX + 22,
      y: pathY - 5,
      color: '#b44c35'
    }
    playerHome.exteriorDoor = { x: playerHome.x, y: playerHome.y - 0.75 }
    playerHome.exteriorReturn = { x: playerHome.x, y: playerHome.y + 0.65 }
    playerHome.sign = { x: playerHome.x + 3.25, y: playerHome.y - 1.35 }

    // The village contains exactly one shop representing each level.
    const shopAreas = [...new Set(['forest', 'graveyard', 'caves', 'emberkeep', 'purpledooms'])]
    const shopPositions = [
      [bounds.minX + 38, pathY - 7],
      [bounds.minX + 45, pathY - 7],
      [bounds.minX + 36, pathY + 8],
      [bounds.minX + 43, pathY + 8],
      [bounds.minX + 50, pathY + 8]
    ]

    this.postgameVillage = {
      unlocked: false,
      pathStartX,
      pathEndX,
      pathY,
      bounds,
      roadX: bounds.minX + 18,
      houses,
      playerHome,
      villagers: [],
      forestGate: { x: pathStartX + 0.5, y: pathY + 0.5 },
      // Leaving the village returns to the safe path beside the player's home.
      forestReturn: { ...this.home.exteriorReturn },
      villageGate: { x: bounds.minX + 0.5, y: pathY + 0.5 },
      villageReturn: { x: bounds.minX + 2, y: pathY + 0.5 },
      shops: shopAreas.map((areaId, index) => ({
        id: `victory_village_${areaId}_shop`,
        areaId,
        shopType: areaId,
        x: shopPositions[index][0],
        y: shopPositions[index][1],
        allStock: true,
        fruitOnly: true,
        postgame: true
      }))
    }

    this.postgameTileKeys = new Set()
    this.villageHousePathOwners = new Map()
    const stamp = (x, y, tile) => {
      const key = `${x},${y}`
      this.tiles.set(key, tile)
      this.postgameTileKeys.add(key)
    }

    // The village is reached through a door transition, not a visible corridor.
    stamp(pathStartX, pathY, BLOCK_TYPES.DOOR)
    for (let x = bounds.minX; x <= bounds.maxX; x++) {
      for (let y = bounds.minY; y <= bounds.maxY; y++) {
        const border = x === bounds.minX || x === bounds.maxX ||
          y === bounds.minY || y === bounds.maxY
        const entrance = x === bounds.minX && y === pathY
        const road = y === pathY || x === this.postgameVillage.roadX
        stamp(
          x,
          y,
          border && !entrance
            ? BLOCK_TYPES.TREE
            : road
              ? BLOCK_TYPES.PATH
              : BLOCK_TYPES.GRASS
        )
      }
    }

    const stampVerticalPath = (x, fromY, toY) => {
      const start = Math.min(fromY, toY)
      const end = Math.max(fromY, toY)
      for (let y = start; y <= end; y++) stamp(x, y, BLOCK_TYPES.PATH)
    }
    const stampHorizontalPath = (y, fromX, toX) => {
      const start = Math.min(fromX, toX)
      const end = Math.max(fromX, toX)
      for (let x = start; x <= end; x++) stamp(x, y, BLOCK_TYPES.PATH)
    }

    // Every building has a visible route back to the village's main roads.
    const bottomLaneY = pathY + 11
    for (const house of houses) {
      const entranceX = Math.floor(house.exteriorReturn.x)
      const entranceY = Math.floor(house.exteriorReturn.y)
      if (house.y < pathY) {
        stampVerticalPath(entranceX, entranceY, pathY)
        for (let y = entranceY; y < pathY; y++) {
          this.villageHousePathOwners.set(`${entranceX},${y}`, house.id)
        }
      } else {
        stampVerticalPath(entranceX, entranceY, bottomLaneY)
        stampHorizontalPath(bottomLaneY, entranceX, this.postgameVillage.roadX)
        for (let y = entranceY; y < bottomLaneY; y++) {
          this.villageHousePathOwners.set(`${entranceX},${y}`, house.id)
        }
      }
    }
    const playerHomeEntranceX = Math.floor(playerHome.exteriorReturn.x)
    const playerHomeEntranceY = Math.floor(playerHome.exteriorReturn.y)
    stampVerticalPath(playerHomeEntranceX, playerHomeEntranceY, pathY)
    for (let y = playerHomeEntranceY; y < pathY; y++) {
      this.villageHousePathOwners.set(`${playerHomeEntranceX},${y}`, playerHome.id)
    }
    for (const shop of this.postgameVillage.shops) {
      stampVerticalPath(Math.floor(shop.x), Math.floor(shop.y) + 1, pathY)
    }

    stamp(bounds.minX, pathY, BLOCK_TYPES.DOOR)
    const villagerStarts = [
      [bounds.minX + 6.5, pathY + 0.5],
      [this.postgameVillage.roadX + 0.5, pathY - 4.5],
      [this.postgameVillage.roadX + 0.5, pathY + 5.5],
      [bounds.minX + 35.5, pathY + 0.5],
      [bounds.minX + 47.5, pathY + 0.5]
    ]
    const speeds = [1.8, 2.05, 2.25, 1.95, 2.15]
    this.postgameVillage.villagers = villagerStarts.map(([x, y], index) => ({
      id: `villager_${index + 1}`,
      homeId: houses[index].id,
      x,
      y,
      targetX: x,
      targetY: y,
      previousX: x,
      previousY: y,
      facing: index % 2 ? 'up' : 'down',
      speed: speeds[index],
      appearanceIndex: index,
      walkTime: 0,
      restTimer: index * 0.22,
      headingHome: false,
      insideHomeId: null,
      homeTimer: 0,
      interiorActivity: 'walking',
      interiorDestinationActivity: 'walking',
      interiorActionTimer: 0,
      interiorActivityIndex: index
    }))

    for (const house of houses) {
      const interior = house.interiorBounds
      for (let x = interior.minX; x <= interior.maxX; x++) {
        for (let y = interior.minY; y <= interior.maxY; y++) {
          const border = x === interior.minX || x === interior.maxX ||
            y === interior.minY || y === interior.maxY
          stamp(x, y, border ? BLOCK_TYPES.HOME_WALL : BLOCK_TYPES.WOOD_FLOOR)
        }
      }
    }
  }

  // --- Phase layouts ---

  buildPhase1(g) {
    // A sealed room serves as the player's home interior.
    g.setVisible(0, 0)

    // Main path down column 1
    g.connect(1, 0, 1, 1)
    g.connect(1, 1, 1, 2)
    g.connect(1, 2, 1, 3)
    g.connect(1, 3, 1, 4)
    g.connect(1, 4, 1, 5)

    // Side rooms
    g.connect(1, 0, 2, 0)
    g.connect(1, 2, 2, 2)
    g.connect(2, 2, 2, 3)
    g.connect(1, 4, 2, 4)

    // Dead-end key branch (left side, must backtrack)
    g.connect(1, 1, 0, 1)
    g.placeKey(0, 1)

    // Locked door on main path (between row 2 and 3)
    g.placeLock(1, 2, 1, 3)
  }

  stampHomeInterior(grid) {
    const cellX = grid.originX
    const cellY = grid.originY
    const size = grid.cellSize
    for (let x = cellX; x <= cellX + size; x++) {
      for (let y = cellY; y <= cellY + size; y++) {
        const border = x === cellX || x === cellX + size ||
          y === cellY || y === cellY + size
        this.tiles.set(
          `${x},${y}`,
          border ? BLOCK_TYPES.HOME_WALL : BLOCK_TYPES.WOOD_FLOOR
        )
        this.protectedTiles.add(`${x},${y}`)
      }
    }
  }

  buildPhase2(g) {
    // Main path snakes through columns 1-2
    g.connect(2, 0, 2, 1)
    g.connect(2, 1, 1, 1)
    g.connect(1, 1, 1, 2)
    g.connect(1, 2, 2, 2)
    g.connect(2, 2, 2, 3)
    g.connect(2, 3, 1, 3)
    g.connect(1, 3, 1, 4)
    g.connect(1, 4, 2, 4)
    g.connect(2, 4, 2, 5)

    // Dead-end 1: key 1 (branch off early)
    g.connect(2, 0, 3, 0)
    g.placeKey(3, 0)

    // Dead-end 2: key 2 (branch off middle)
    g.connect(2, 3, 3, 3)
    g.placeKey(3, 3)

    // Lock 1: on main path between row 1->2
    g.placeLock(1, 1, 1, 2)

    // Lock 2: on main path between row 3->4
    g.placeLock(1, 3, 1, 4)
  }

  buildPhase3(g) {
    // Main descent through the crystal caverns.
    g.connect(2, 0, 2, 1)
    g.connect(2, 1, 1, 1)
    g.connect(1, 1, 1, 2)
    g.connect(1, 2, 2, 2)
    g.connect(2, 2, 2, 3)
    g.connect(2, 3, 3, 3)
    g.connect(3, 3, 3, 4)
    g.connect(3, 4, 2, 4)
    g.connect(2, 4, 2, 5)
    g.connect(2, 5, 2, 6)

    // Map chamber, reached early but requiring a deliberate detour.
    g.connect(2, 1, 3, 1)
    g.connect(3, 1, 4, 1)

    // Keys live in opposite side chambers before their matching gates.
    g.connect(1, 2, 0, 2)
    g.placeKey(0, 2)
    g.placeLock(2, 2, 2, 3)

    g.connect(2, 4, 1, 4)
    g.placeKey(1, 4)
    g.placeLock(2, 5, 2, 6)

    // Additional exploration loop with a chest-friendly dead end.
    g.connect(1, 4, 1, 5)
    g.connect(1, 5, 0, 5)
  }

  buildPhase4(g) {
    // Main route through the ruined keep.
    g.connect(2, 0, 2, 1)
    g.connect(2, 1, 3, 1)
    g.connect(3, 1, 3, 2)
    g.connect(3, 2, 2, 2)
    g.connect(2, 2, 2, 3)
    g.connect(2, 3, 1, 3)
    g.connect(1, 3, 1, 4)
    g.connect(1, 4, 2, 4)
    g.connect(2, 4, 2, 5)

    // Map tower.
    g.connect(3, 1, 4, 1)

    // Opposing key rooms gate the keep's central and final halls.
    g.connect(2, 1, 1, 1)
    g.placeKey(1, 1)
    g.placeLock(3, 2, 2, 2)

    g.connect(1, 3, 0, 3)
    g.placeKey(0, 3)
    g.placeLock(2, 4, 2, 5)

    // A small battlement branch for random treasure.
    g.connect(2, 3, 3, 3)
    g.connect(3, 3, 4, 3)
  }

  buildPhase5(g) {
    // Purple Dooms winds toward a sealed throne room.
    g.connect(2, 0, 2, 1)
    g.connect(2, 1, 1, 1)
    g.connect(1, 1, 1, 2)
    g.connect(1, 2, 2, 2)
    g.connect(2, 2, 3, 2)
    g.connect(3, 2, 3, 3)
    g.connect(3, 3, 2, 3)
    g.connect(2, 3, 2, 4)
    g.connect(2, 4, 2, 5)

    // The final map lies in an exposed eastern watch room.
    g.connect(2, 1, 3, 1)
    g.connect(3, 1, 4, 1)

    // Keys force exploration before the final confrontation.
    g.connect(1, 2, 0, 2)
    g.placeKey(0, 2)
    g.placeLock(2, 2, 3, 2)

    g.connect(3, 3, 4, 3)
    g.placeKey(4, 3)
    g.placeLock(2, 4, 2, 5)

    // Side chambers provide recovery space before the throne room.
    g.connect(2, 3, 1, 3)
    g.connect(1, 3, 1, 4)
  }

  buildQuestBranches() {
    this.questBranches = QUEST_BRANCHES.map(definition => {
      const gridIndex = this.grids.findIndex(grid => grid.areaId === definition.areaId)
      const grid = this.grids[gridIndex]
      if (!grid) {
        throw new Error(`Quest branch "${definition.id}" references unknown area "${definition.areaId}"`)
      }

      let previous = definition.attachment
      for (const cell of definition.cells) {
        grid.connect(previous[0], previous[1], cell[0], cell[1])
        previous = cell
      }

      const rewardCell = definition.cells[definition.cells.length - 1]
      const rewardPosition = grid.getCellCenter(rewardCell[0], rewardCell[1])
      const enemySpawns = []

      definition.encounters.forEach((encounter, encounterIndex) => {
        const center = grid.getCellCenter(encounter.cell[0], encounter.cell[1])
        encounter.enemies.forEach((type, enemyIndex) => {
          const offset = QUEST_ENCOUNTER_OFFSETS[
            encounter.enemies.length === 1 ? 0 : enemyIndex % QUEST_ENCOUNTER_OFFSETS.length
          ]
          enemySpawns.push({
            id: `${definition.areaId}_quest_${definition.id}_${encounterIndex}_${enemyIndex}`,
            type,
            x: center.x + offset.x,
            y: center.y + offset.y
          })
        })
      })

      return {
        id: definition.id,
        areaId: definition.areaId,
        title: definition.title,
        gridIndex,
        attachment: {
          col: definition.attachment[0],
          row: definition.attachment[1]
        },
        cells: definition.cells.map(([col, row]) => ({ col, row })),
        reward: { ...definition.reward },
        rewardCell: { col: rewardCell[0], row: rewardCell[1] },
        chest: {
          id: `quest_chest_${definition.id}`,
          x: rewardPosition.x,
          y: rewardPosition.y
        },
        enemySpawns
      }
    })
  }

  buildKeyChests() {
    this.keyChests = this.grids.flatMap(grid => (
      grid.cells.flatMap((column, col) => (
        column.flatMap((cell, row) => {
          if (!cell.hasKey) return []
          const position = grid.getCellCenter(col, row)
          const zone = this.zones.find(candidate => candidate.name === grid.areaId)
          const enemyTypes = zone?.enemyTypes?.filter(type => (
            type !== 'darkWizard' || grid.areaId === 'graveyard'
          )) || ['zombie']
          return [{
            id: `key_chest_${grid.areaId}_${col}_${row}`,
            areaId: grid.areaId,
            isDarkKingKey: grid.areaId === 'purpledooms' && col === 4 && row === 3,
            x: position.x,
            y: position.y,
            enemySpawns: [
              {
                id: `key_guard_${grid.areaId}_${col}_${row}_1`,
                type: enemyTypes[0],
                x: position.x - 1.5,
                y: position.y + 1.5
              },
              {
                id: `key_guard_${grid.areaId}_${col}_${row}_2`,
                type: enemyTypes[1 % enemyTypes.length],
                x: position.x + 1.5,
                y: position.y + 1.5
              }
            ]
          }]
        })
      ))
    ))
  }

  getQuestBranchesForArea(areaId) {
    return this.questBranches.filter(branch => branch.areaId === areaId)
  }

  buildPhaseTransition(g1, g2, exitCol, enterCol) {
    // Open the shared wall between the two grids.
    // g1's bottom cell at exitCol opens south, g2's top cell at enterCol opens north.
    g1.openSouthExit(exitCol)
    g2.openNorthEntrance(enterCol)

    // The shared wall row is at g1.originY + g1.rows * cellSize = g2.originY.
    // g1's last-row cell stamps a bottom wall there, and g2's first-row cell stamps
    // a top wall there. Both will carve the door opening since walls.s / walls.n are false.
    // We don't need a corridor — the grids are flush.
    this.transitionCorridor = null
  }

  decorateCrystalCaves(grid) {
    for (let col = 0; col < grid.cols; col++) {
      for (let row = 0; row < grid.rows; row++) {
        const cell = grid.getCell(col, row)
        if (!cell?.visible) continue

        const center = grid.getCellCenter(col, row)
        const accents = (col + row) % 2 === 0
          ? [
              { x: center.x - 2, y: center.y - 2, tile: BLOCK_TYPES.DIAMOND_ORE },
              { x: center.x + 2, y: center.y + 2, tile: BLOCK_TYPES.IRON_ORE }
            ]
          : [
              { x: center.x + 2, y: center.y - 2, tile: BLOCK_TYPES.MYSTIC_STONE },
              { x: center.x - 2, y: center.y + 2, tile: BLOCK_TYPES.COBBLESTONE }
            ]

        for (const accent of accents) {
          const key = `${accent.x},${accent.y}`
          this.tiles.set(key, accent.tile)
          this.protectedTiles.add(key)
        }
      }
    }
  }

  decorateEmberKeep(grid) {
    this.decorateAreaFloor(grid, [
      BLOCK_TYPES.BRICK,
      BLOCK_TYPES.ASH,
      BLOCK_TYPES.LAVA,
      BLOCK_TYPES.DARK_BRICK
    ])
  }

  decoratePurpleDooms(grid) {
    this.decorateAreaFloor(grid, [
      BLOCK_TYPES.MYSTIC_STONE,
      BLOCK_TYPES.DARK_STONE,
      BLOCK_TYPES.OBSIDIAN,
      BLOCK_TYPES.DARK_BRICK
    ])
  }

  decorateAreaFloor(grid, palette) {
    for (let col = 0; col < grid.cols; col++) {
      for (let row = 0; row < grid.rows; row++) {
        const cell = grid.getCell(col, row)
        if (!cell?.visible) continue

        const center = grid.getCellCenter(col, row)
        const positions = [
          { x: center.x - 2, y: center.y - 2 },
          { x: center.x + 2, y: center.y + 2 }
        ]

        positions.forEach((position, index) => {
          const tile = palette[(col + row + index) % palette.length]
          const key = `${position.x},${position.y}`
          this.tiles.set(key, tile)
          this.protectedTiles.add(key)
        })
      }
    }
  }

  buildAreaBushes() {
    const palettes = {
      forest: ['#245f32', '#3f8b42', '#75ae4f'],
      graveyard: ['#403747', '#65566e', '#8b7891'],
      caves: ['#285567', '#3f8395', '#78c4c8'],
      emberkeep: ['#572d27', '#9a4933', '#dc783d'],
      purpledooms: ['#3b2450', '#663987', '#aa68c4']
    }
    // Keep the complete bush sprite on floor tiles. The room's top and left
    // wall occupy the first world unit, so positions closer to the edge can
    // have a non-solid anchor while their leaves still overlap the wall.
    const offsets = [
      { x: -2.25, y: 2.2 },
      { x: 2.25, y: -2.05 }
    ]

    for (let gridIndex = 0; gridIndex < this.grids.length; gridIndex++) {
      const grid = this.grids[gridIndex]
      for (let col = 0; col < grid.cols; col++) {
        for (let row = 0; row < grid.rows; row++) {
          const cell = grid.getCell(col, row)
          if (!cell?.visible) continue
          if (gridIndex === 0 && col === 0 && row === 0) continue
          const center = grid.getCellCenter(col, row)
          const offset = offsets[(col + row) % offsets.length]
          this.bushes.push({
            x: center.x + offset.x,
            y: center.y + offset.y,
            areaId: grid.areaId,
            colors: palettes[grid.areaId] || palettes.forest,
            kind: (col * 3 + row) % 3
          })
        }
      }
    }
  }

  stampAllGrids() {
    for (const grid of this.grids) {
      const floorFn = (x, y) => this.getBaseTerrain(grid.terrainType, x, y)
      const wallTile = grid.terrainType === 'forest'
        ? BLOCK_TYPES.TREE
        : BLOCK_TYPES.WALL
      grid.stampToTiles(this.tiles, this.protectedTiles, floorFn, wallTile)
    }

    // No separate transition corridor needed — grids are flush.
  }

  getBaseTerrain(terrainType, x, y) {
    switch (terrainType) {
      case 'forest':
        return Math.random() < 0.05 ? BLOCK_TYPES.DIRT : BLOCK_TYPES.GRASS
      case 'graveyard':
        return Math.random() < 0.3 ? BLOCK_TYPES.GRAVEL : BLOCK_TYPES.DIRT
      case 'caves':
        return Math.random() < 0.2 ? BLOCK_TYPES.COBBLESTONE : BLOCK_TYPES.STONE
      case 'swamp':
        return Math.random() < 0.15 ? BLOCK_TYPES.WATER : BLOCK_TYPES.DIRT
      case 'castle':
        return Math.random() < 0.3 ? BLOCK_TYPES.BRICK : BLOCK_TYPES.COBBLESTONE
      case 'shadow':
        return Math.random() < 0.2 ? BLOCK_TYPES.MYSTIC_STONE : BLOCK_TYPES.DARK_STONE
      case 'fortress':
        return Math.random() < 0.3 ? BLOCK_TYPES.ASH : BLOCK_TYPES.DARK_BRICK
      case 'throne':
        return BLOCK_TYPES.OBSIDIAN
      default:
        return BLOCK_TYPES.GRASS
    }
  }

  // --- Tile access ---

  getCellBounds(grid, col, row) {
    const minX = grid.originX + col * grid.cellSize
    const minY = grid.originY + row * grid.cellSize
    return {
      minX,
      maxX: minX + grid.cellSize,
      minY,
      maxY: minY + grid.cellSize
    }
  }

  getGridCellForPosition(x, y) {
    const tx = Math.floor(x)
    const ty = Math.floor(y)

    for (let gridIndex = 0; gridIndex < this.grids.length; gridIndex++) {
      const grid = this.grids[gridIndex]
      const minX = grid.originX
      const maxX = grid.originX + grid.cols * grid.cellSize
      const minY = grid.originY
      const maxY = grid.originY + grid.rows * grid.cellSize

      if (tx < minX || tx > maxX || ty < minY || ty > maxY) continue

      const col = Math.floor((tx - grid.originX) / grid.cellSize)
      const row = Math.floor((ty - grid.originY) / grid.cellSize)
      if (col < 0 || col >= grid.cols || row < 0 || row >= grid.rows) continue

      return { gridIndex, col, row }
    }

    return null
  }

  getRoomIdForPosition(x, y) {
    const cell = this.getGridCellForPosition(x, y)
    if (!cell) return null
    return `${cell.gridIndex}:${cell.col},${cell.row}`
  }

  isRoomActiveAt(x, y) {
    if (!this.game.player) return false
    const playerRoom = this.getRoomIdForPosition(this.game.player.x, this.game.player.y)
    const targetRoom = this.getRoomIdForPosition(x, y)
    return playerRoom !== null && playerRoom === targetRoom
  }

  resetAreaMaps() {
    this.areaMaps = new Map(
      this.grids.map(grid => [
        grid.areaId,
        {
          mapFound: false,
          exploredCells: new Set()
        }
      ])
    )
    this.revealedTiles.clear()
  }

  getAreaMapState(areaId) {
    return this.areaMaps.get(areaId) || null
  }

  discoverAreaMap(areaId) {
    const areaMap = this.getAreaMapState(areaId)
    if (!areaMap || areaMap.mapFound) return false
    areaMap.mapFound = true
    return true
  }

  markCellVisited(gridIndex, col, row) {
    const grid = this.grids[gridIndex]
    if (!grid) return
    const cell = grid.getCell(col, row)
    if (!cell || !cell.visible) return

    const areaMap = this.getAreaMapState(grid.areaId)
    if (!areaMap) return

    const key = `${col},${row}`
    if (areaMap.exploredCells.has(key)) return
    areaMap.exploredCells.add(key)
    this.revealCellTiles(grid, col, row)
  }

  revealCellTiles(grid, col, row) {
    const cs = grid.cellSize
    const startX = grid.originX + col * cs
    const startY = grid.originY + row * cs
    for (let x = startX; x <= startX + cs; x++) {
      for (let y = startY; y <= startY + cs; y++) {
        this.revealedTiles.add(`${x},${y}`)
      }
    }
  }

  rebuildRevealedTiles() {
    this.revealedTiles.clear()
    for (const grid of this.grids) {
      const areaMap = this.getAreaMapState(grid.areaId)
      if (!areaMap) continue
      for (const key of areaMap.exploredCells) {
        const [col, row] = key.split(',').map(Number)
        this.revealCellTiles(grid, col, row)
      }
    }
  }

  markVisitedFromPosition(x, y) {
    const cell = this.getGridCellForPosition(x, y)
    if (!cell) return
    this.markCellVisited(cell.gridIndex, cell.col, cell.row)
  }

  getAreaExplorationProgress(areaId) {
    const grid = this.grids.find(candidate => candidate.areaId === areaId)
    const areaMap = this.getAreaMapState(areaId)
    if (!grid || !areaMap) return { explored: 0, total: 0, remaining: 0, complete: false }
    const total = grid.cells.flat().filter(cell => cell?.visible).length
    const explored = Array.from(areaMap.exploredCells).filter(key => {
      const [col, row] = key.split(',').map(Number)
      return grid.getCell(col, row)?.visible === true
    }).length
    return {
      explored,
      total,
      remaining: Math.max(0, total - explored),
      complete: total > 0 && explored >= total
    }
  }

  getLevelAdvanceExit(areaId) {
    const gridIndex = this.grids.findIndex(grid => grid.areaId === areaId)
    if (gridIndex < 0 || gridIndex >= this.grids.length - 1) return null
    const grid = this.grids[gridIndex]
    const col = Array.from({ length: grid.cols }, (_, index) => index)
      .find(index => grid.getCell(index, grid.rows - 1)?.walls.s === false)
    if (col === undefined) return null
    return {
      areaId,
      gridIndex,
      col,
      row: grid.rows - 1,
      x: grid.getCellCenter(col, grid.rows - 1).x,
      y: this.grids[gridIndex + 1].originY
    }
  }

  isLevelAdvanceLockedAt(x, y) {
    const activeAreaId = this.getActiveRenderAreaId()
    const exit = this.getLevelAdvanceExit(activeAreaId)
    if (!exit || this.getAreaExplorationProgress(activeAreaId).complete) return false
    return Math.floor(x) === Math.floor(exit.x) && Math.floor(y) === Math.floor(exit.y)
  }

  notifyLevelAdvanceLocked() {
    const areaId = this.getActiveRenderAreaId()
    const progress = this.getAreaExplorationProgress(areaId)
    if (progress.complete || progress.remaining <= 0) return false
    if (this.exitLockNoticeAreaId === areaId) return true
    this.exitLockNoticeAreaId = areaId
    const roomWord = progress.remaining === 1 ? 'ROOM' : 'ROOMS'
    this.game.showItemPopup?.(
      `EXPLORE ${progress.remaining} MORE ${roomWord} TO ENTER THE NEXT LEVEL.`,
      null
    )
    return true
  }

  getMinimapStateAt(x, y) {
    const village = this.postgameVillage
    if (village?.unlocked &&
        x >= village.bounds.minX && x <= village.bounds.maxX &&
        y >= village.bounds.minY && y <= village.bounds.maxY) {
      return {
        kind: 'village',
        areaId: 'village',
        displayName: 'Village',
        cols: 1,
        rows: 1,
        currentCell: { col: 0, row: 0 },
        mapFound: true,
        exploredCount: 1,
        cells: [{ col: 0, row: 0, explored: true }],
        connections: [],
        bounds: { ...village.bounds },
        player: { x, y },
        houses: [
          ...village.houses.map(house => ({ x: house.x, y: house.y })),
          { x: village.playerHome.x, y: village.playerHome.y, playerHome: true }
        ],
        shops: village.shops.map(shop => ({ x: shop.x, y: shop.y }))
      }
    }
    const currentCell = this.getGridCellForPosition(x, y)
    if (!currentCell) return null

    const grid = this.grids[currentCell.gridIndex]
    if (!grid) return null
    const areaMap = this.getAreaMapState(grid.areaId)
    if (!areaMap) return null

    const cells = []
    const connections = []

    for (let col = 0; col < grid.cols; col++) {
      for (let row = 0; row < grid.rows; row++) {
        const cell = grid.getCell(col, row)
        if (!cell?.visible) continue

        const key = `${col},${row}`
        cells.push({
          col,
          row,
          explored: areaMap.exploredCells.has(key)
        })

        for (const direction of ['e', 's']) {
          if (cell.walls[direction]) continue
          const dc = direction === 'e' ? 1 : 0
          const dr = direction === 's' ? 1 : 0
          const neighbor = grid.getCell(col + dc, row + dr)
          if (!neighbor?.visible) continue
          connections.push({
            col,
            row,
            toCol: col + dc,
            toRow: row + dr,
            direction,
            locked: this.isGridConnectionLocked(grid, col, row, direction),
            exit: false
          })
        }

        for (const direction of ['n', 'e', 's', 'w']) {
          if (cell.walls[direction]) continue
          const dc = direction === 'e' ? 1 : direction === 'w' ? -1 : 0
          const dr = direction === 's' ? 1 : direction === 'n' ? -1 : 0
          const neighbor = grid.getCell(col + dc, row + dr)
          if (neighbor?.visible) continue
          connections.push({
            col,
            row,
            toCol: null,
            toRow: null,
            direction,
            locked: direction === 's' &&
              this.getLevelAdvanceExit(grid.areaId)?.col === col &&
              !this.getAreaExplorationProgress(grid.areaId).complete,
            exit: true
          })
        }
      }
    }

    const zone = this.zones.find(candidate => candidate.name === grid.areaId)
    const homeCell = grid.areaId === 'forest' && !this.postgameVillage?.unlocked
      ? this.getGridCellForPosition(this.home.exteriorDoor.x, this.home.exteriorDoor.y)
      : null
    const shop = this.postgameVillage?.unlocked
      ? null
      : this.shopSpawns.find(candidate => (
          !candidate.postgame && candidate.areaId === grid.areaId
        ))
    const shopGridCell = shop
      ? this.getGridCellForPosition(shop.x, shop.y)
      : null
    const shopKey = shopGridCell
      ? `${shopGridCell.col},${shopGridCell.row}`
      : null
    return {
      areaId: grid.areaId,
      displayName: zone?.displayName || grid.areaId,
      cols: grid.cols,
      rows: grid.rows,
      cellSize: grid.cellSize,
      originX: grid.originX,
      originY: grid.originY,
      currentCell: {
        col: currentCell.col,
        row: currentCell.row
      },
      homeCell: homeCell?.gridIndex === currentCell.gridIndex
        ? { col: homeCell.col, row: homeCell.row }
        : null,
      shopCell: shopGridCell?.gridIndex === currentCell.gridIndex &&
        areaMap.exploredCells.has(shopKey)
        ? { col: shopGridCell.col, row: shopGridCell.row }
        : null,
      mapFound: areaMap.mapFound,
      exploredCount: areaMap.exploredCells.size,
      cells,
      connections
    }
  }

  getMinimapStateForArea(areaId) {
    if (areaId === 'village' && this.postgameVillage?.unlocked) {
      const village = this.postgameVillage
      const state = this.getMinimapStateAt(
        (village.bounds.minX + village.bounds.maxX) / 2,
        village.pathY
      )
      if (state) state.browsing = true
      return state
    }
    const gridIndex = this.grids.findIndex(grid => grid.areaId === areaId)
    const grid = this.grids[gridIndex]
    if (!grid) return null
    const areaMap = this.getAreaMapState(areaId)
    if (!areaMap?.mapFound) return null

    const explored = Array.from(areaMap.exploredCells)[0]?.split(',').map(Number)
    let col = explored?.[0]
    let row = explored?.[1]
    if (!Number.isInteger(col) || !Number.isInteger(row) || !grid.getCell(col, row)?.visible) {
      let firstVisible = null
      for (let candidateCol = 0; candidateCol < grid.cols && !firstVisible; candidateCol++) {
        for (let candidateRow = 0; candidateRow < grid.rows; candidateRow++) {
          if (grid.getCell(candidateCol, candidateRow)?.visible) {
            firstVisible = { col: candidateCol, row: candidateRow }
            break
          }
        }
      }
      if (!firstVisible) return null
      col = firstVisible.col
      row = firstVisible.row
    }

    const center = grid.getCellCenter(col, row)
    const state = this.getMinimapStateAt(center.x, center.y)
    if (state) state.browsing = true
    return state
  }

  isGridConnectionLocked(grid, col, row, direction) {
    const cell = grid.getCell(col, row)
    if (!cell?.lockedWalls[direction]) return false

    const cellX = grid.originX + col * grid.cellSize
    const cellY = grid.originY + row * grid.cellSize
    const half = Math.floor(grid.cellSize / 2)
    const positions = {
      n: { x: cellX + half, y: cellY },
      e: { x: cellX + grid.cellSize, y: cellY + half },
      s: { x: cellX + half, y: cellY + grid.cellSize },
      w: { x: cellX, y: cellY + half }
    }
    const position = positions[direction]
    return position ? this.getTileAt(position.x, position.y) === BLOCK_TYPES.LOCKED_DOOR : false
  }

  isTileRevealed(x, y) {
    const key = `${Math.floor(x)},${Math.floor(y)}`
    return this.revealedTiles.has(key) ||
      (this.postgameVillage?.unlocked && this.postgameTileKeys?.has(key))
  }

  getTileAt(x, y) {
    const key = `${Math.floor(x)},${Math.floor(y)}`
    if (this.modifiedTiles.has(key)) return this.modifiedTiles.get(key)
    return this.tiles.get(key) || BLOCK_TYPES.AIR
  }

  setTileAt(x, y, tileType) {
    const key = `${Math.floor(x)},${Math.floor(y)}`
    this.modifiedTiles.set(key, tileType)
    this.tiles.set(key, tileType)
  }

  clearTileToGround(x, y) {
    const zone = this.getZoneAt(x, y)
    const groundTile = zone ? this.getBaseTerrain(zone.terrainType, x, y) : BLOCK_TYPES.GRASS
    this.setTileAt(x, y, groundTile)
  }

  unlockDoorAt(x, y) {
    this.setTileAt(x, y, BLOCK_TYPES.DOOR)
    this.protectedTiles.add(`${Math.floor(x)},${Math.floor(y)}`)
  }

  isDarkKingDoorAt(x, y) {
    const grid = this.grids.find(candidate => candidate.areaId === 'purpledooms')
    if (!grid) return false
    const entrance = grid.getCellCenter(2, 4)
    const doorY = grid.originY + 5 * grid.cellSize
    return Math.floor(x) === Math.floor(entrance.x) &&
      Math.floor(y) === Math.floor(doorY)
  }

  isSolid(x, y) {
    const tx = Math.floor(x)
    const ty = Math.floor(y)
    const tileKey = `${tx},${ty}`

    if (this.postgameTileKeys?.has(tileKey) && !this.postgameVillage?.unlocked) {
      return true
    }

    // Outside any zone or transition corridor = blocked.
    const insideAnyZone = this.zones.some(zone => (
      tx >= zone.bounds.minX && tx <= zone.bounds.maxX &&
      ty >= zone.bounds.minY && ty <= zone.bounds.maxY
    ))

    const postgameTile = this.postgameVillage?.unlocked &&
      this.postgameTileKeys?.has(tileKey)
    if (!insideAnyZone && !postgameTile) return true

    const tile = this.getTileAt(x, y)
    return isBlockSolid(tile)
  }

  unlockPostgameVillage() {
    if (!this.postgameVillage) return false
    this.postgameVillage.unlocked = true
    return true
  }

  completeDarkKingVictory() {
    this.unlockPostgameVillage()
    this.game.enemyManager?.clearAllEnemies?.()
    this.darkKingRetryHealth = null
    const player = this.game.player
    if (!player) return false
    player.x = this.home.interiorSpawn.x
    player.y = this.home.interiorSpawn.y
    if (player.velocity) {
      player.velocity.x = 0
      player.velocity.y = 0
    }
    player.isSitting = false
    player.isSleeping = false
    this.home.sleepTimer = null
    this.markVisitedFromPosition(player.x, player.y)
    return true
  }

  isInSpawnSafeZone(x, y) {
    const spawn = this.zones[0]?.spawnPoint || { x: 0, y: 0 }
    return Math.abs(x - spawn.x) < 8 && Math.abs(y - spawn.y) < 8
  }

  isEnemySpawnBlocked(x, y) {
    for (const area of this.enemySpawnBlockedAreas) {
      if (x >= area.minX && x <= area.maxX && y >= area.minY && y <= area.maxY) {
        return true
      }
    }
    return false
  }

  getZoneAt(x, y) {
    for (const zone of this.zones) {
      if (zone.containsPoint(x, y)) return zone
    }

    // Side rooms and connecting passages can extend beyond a zone's main grid
    // bounds. Associate those spaces with the nearest level instead of
    // incorrectly treating every out-of-bounds position as the forest.
    return this.zones.reduce((nearest, zone) => {
      const dx = Math.max(zone.bounds.minX - x, 0, x - zone.bounds.maxX)
      const dy = Math.max(zone.bounds.minY - y, 0, y - zone.bounds.maxY)
      const distanceSquared = dx * dx + dy * dy
      return !nearest || distanceSquared < nearest.distanceSquared
        ? { zone, distanceSquared }
        : nearest
    }, null)?.zone || null
  }

  getCurrentZone(position) {
    return this.getZoneAt(position.x, position.y)
  }

  getDeathRespawnPoint(x, y) {
    const cell = this.getGridCellForPosition(x, y)
    const bossCell = this.getGridCellForPosition(this.bossSpawn.x, this.bossSpawn.y)
    const darkKingActive = this.game.enemyManager?.enemies?.some(enemy => (
      enemy.type === 'darkKing' && enemy.isAlive && enemy.encounterActive
    ))
    const diedInBossRoom = cell && bossCell &&
      cell.gridIndex === bossCell.gridIndex &&
      cell.col === bossCell.col &&
      cell.row === bossCell.row

    if (!darkKingActive && !diedInBossRoom) return null

    const darkKing = this.game.enemyManager?.enemies?.find(enemy => (
      enemy.type === 'darkKing' && enemy.isAlive
    ))
    if (Number.isFinite(darkKing?.health) && darkKing.health > 0) {
      this.darkKingRetryHealth = darkKing.health
    }
    return { ...this.darkKingCheckpoint }
  }

  isInsideHome(x, y) {
    const cell = this.getGridCellForPosition(x, y)
    return cell?.gridIndex === 0 && cell.col === 0 && cell.row === 0
  }

  getHomeExteriorDoor() {
    return this.postgameVillage?.unlocked
      ? this.postgameVillage.playerHome.exteriorDoor
      : this.home.exteriorDoor
  }

  getHomeExteriorReturn() {
    return this.postgameVillage?.unlocked
      ? this.postgameVillage.playerHome.exteriorReturn
      : this.home.exteriorReturn
  }

  getHomeInteriorBounds() {
    return this.getCellBounds(this.grids[0], 0, 0)
  }

  isPlayerLoadPositionBlocked(x, y, radius = 0.4) {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return true

    const checkPoints = [
      { x: x - radius, y: y - radius },
      { x: x + radius, y: y - radius },
      { x: x - radius, y: y + radius },
      { x: x + radius, y: y + radius }
    ]
    if (checkPoints.some(point => this.isSolid(point.x, point.y))) return true
    if (this.collidesWithHome(x, y, radius)) return true
    return this.collidesWithPostgameVillage(x, y, radius)
  }

  getSafePlayerLoadPosition(x, y, preferredLevelIndex = 0) {
    if (!this.isPlayerLoadPositionBlocked(x, y)) return { x, y }

    // Furniture states are intentionally transient and are not restored from
    // saves. Never leave a restored player embedded in the couch, bed,
    // fireplace, table, door, or a building that moved between versions.
    if (Number.isFinite(x) && Number.isFinite(y)) {
      if (this.isInsideHome(x, y)) return { ...this.home.interiorSpawn }

      const villageHouse = this.getVillageHouseInteriorAt(x, y)
      if (villageHouse) return { ...villageHouse.interiorSpawn }

      const village = this.postgameVillage
      if (
        village?.unlocked &&
        x >= village.bounds.minX && x <= village.bounds.maxX &&
        y >= village.bounds.minY && y <= village.bounds.maxY
      ) {
        return { ...village.villageReturn }
      }
    }

    // Invalid coordinates and obsolete world geometry recover to an authored
    // checkpoint instead of producing an inescapable or unrenderable save.
    if (this.postgameVillage?.unlocked) return { ...this.home.interiorSpawn }
    const levelIndex = Number.isInteger(preferredLevelIndex)
      ? Math.max(0, Math.min(this.zones.length - 1, preferredLevelIndex))
      : 0
    return { ...(this.zones[levelIndex]?.spawnPoint || this.home.interiorSpawn) }
  }

  enterHome() {
    const player = this.game.player
    if (!player || this.isInsideHome(player.x, player.y)) return false
    player.x = this.home.interiorSpawn.x
    player.y = this.home.interiorSpawn.y
    player.velocity = { x: 0, y: 0 }
    this.markVisitedFromPosition(player.x, player.y)
    this.game.quickSave?.()
    return true
  }

  sendPlayerHome() {
    const player = this.game.player
    if (!player || this.isInsideHome(player.x, player.y)) return false
    player.isSitting = false
    player.isSleeping = false
    player.sittingVillageHouseId = null
    player.x = this.home.interiorSpawn.x
    player.y = this.home.interiorSpawn.y
    player.velocity = { x: 0, y: 0 }
    this.home.sleepTimer = null
    this.markVisitedFromPosition(player.x, player.y)
    this.game.quickSave?.()
    return true
  }

  exitHomeThroughDoor() {
    const player = this.game.player
    if (!player || !this.isInsideHome(player.x, player.y)) return false
    const exteriorReturn = this.getHomeExteriorReturn()
    player.x = exteriorReturn.x
    player.y = exteriorReturn.y
    player.velocity = { x: 0, y: 0 }
    this.home.doorUnlocked = false
    this.game.quickSave?.()
    return true
  }

  interactWithHome(maxRange = 2) {
    const player = this.game.player
    if (!player) return false

    if (!this.isInsideHome(player.x, player.y)) {
      const exteriorDoor = this.getHomeExteriorDoor()
      if (Math.hypot(player.x - exteriorDoor.x, player.y - exteriorDoor.y) > maxRange) {
        return false
      }
      if (!this.home.doorUnlocked && !this.home.hasKey) {
        this.game.showItemPopup?.('The house is locked. You need the Home Key.')
        return true
      }
      this.home.doorUnlocked = true
      return this.enterHome()
    }

    const interactions = [
      { kind: 'door', position: this.home.exitDoor },
      { kind: 'couch', position: this.home.couch },
      { kind: 'bed', position: this.home.bed }
    ]
    if (!this.home.hasKey) {
      interactions.push({ kind: 'key', position: this.home.key })
    }
    const nearest = interactions
      .map(interaction => ({
        ...interaction,
        distance: Math.hypot(
          player.x - interaction.position.x,
          player.y - interaction.position.y
        )
      }))
      .filter(interaction => interaction.distance <= maxRange)
      .sort((a, b) => a.distance - b.distance)[0]

    if (!nearest) return false
    if (nearest.kind === 'door') {
      if (!this.home.doorUnlocked && !this.home.hasKey) {
        this.game.showItemPopup?.('The orange door is locked. Find the Home Key on the right.')
        return true
      }
      this.home.doorUnlocked = true
      return this.exitHomeThroughDoor()
    }
    if (nearest.kind === 'key') {
      return this.collectHomeKey(true)
    }
    if (nearest.kind === 'couch') {
      player.x = this.home.couch.x
      player.y = this.home.couch.y + 0.12
      player.facing = 'down'
      player.isSitting = true
      player.isSleeping = false
      return true
    }
    if (nearest.kind === 'bed') {
      player.x = this.home.bed.x
      player.y = this.home.bed.y
      player.facing = 'right'
      player.isSitting = false
      player.isSleeping = true
      this.home.sleepTimer = 0
      return true
    }
    return false
  }

  collectHomeKey(showMessage = false) {
    if (this.home.hasKey) return false
    this.home.hasKey = true
    this.game.quickSave?.()
    if (showMessage) {
      this.game.showItemPopup?.('You found the Home Key. It opens the orange door.')
    }
    return true
  }

  interactWithHomeDoorAt(targetX, targetY, maxRange = 2) {
    const player = this.game.player
    if (!player) return false
    const door = this.isInsideHome(player.x, player.y)
      ? this.home.exitDoor
      : this.getHomeExteriorDoor()
    if (Math.hypot(targetX - door.x, targetY - door.y) > 1.5) return false
    if (Math.hypot(player.x - door.x, player.y - door.y) > maxRange) return false
    return this.interactWithHome(maxRange)
  }

  getVillageHouseInteriorAt(x, y) {
    if (!this.postgameVillage?.unlocked) return null
    return this.postgameVillage.houses.find(house => {
      const bounds = house.interiorBounds
      return x >= bounds.minX && x <= bounds.maxX &&
        y >= bounds.minY && y <= bounds.maxY
    }) || null
  }

  interactWithVillageHouse(maxRange = 2) {
    const player = this.game.player
    if (!player || !this.postgameVillage?.unlocked) return false

    const interiorHouse = this.getVillageHouseInteriorAt(player.x, player.y)
    if (interiorHouse) {
      const centerX = (interiorHouse.interiorBounds.minX + interiorHouse.interiorBounds.maxX) / 2
      const minY = interiorHouse.interiorBounds.minY
      const at = ([offsetX, offsetY]) => ({ x: centerX + offsetX, y: minY + offsetY })
      const interactions = [
        { kind: 'door', position: interiorHouse.exitDoor, halfWidth: 0.65, halfHeight: 0.85 },
        { kind: 'couch', position: at(interiorHouse.furniture.couch), halfWidth: 1.5, halfHeight: 0.55 },
        { kind: 'bed', position: at(interiorHouse.furniture.bed), halfWidth: 0.65, halfHeight: 0.64 }
      ]
      const nearest = interactions
        .map(interaction => ({
          ...interaction,
          distance: Math.hypot(
            Math.max(0, Math.abs(player.x - interaction.position.x) - interaction.halfWidth),
            Math.max(0, Math.abs(player.y - interaction.position.y) - interaction.halfHeight)
          )
        }))
        .filter(interaction => interaction.distance <= maxRange)
        .sort((a, b) => a.distance - b.distance)[0]
      if (!nearest) return false
      if (nearest.kind === 'couch') {
        player.x = nearest.position.x
        player.y = nearest.position.y + 0.12
        player.facing = 'down'
        player.isSitting = true
        player.isSleeping = false
        player.sittingVillageHouseId = interiorHouse.id
        return true
      }
      if (nearest.kind === 'bed') {
        // Village beds are not interactive. Consume the context action so it
        // does not fall through to an attack, but show no message or effect.
        return true
      }
      player.x = interiorHouse.exteriorReturn.x
      player.y = interiorHouse.exteriorReturn.y
      player.velocity = { x: 0, y: 0 }
      this.game.quickSave?.()
      return true
    }

    const house = this.postgameVillage.houses
      .map(candidate => ({
        ...candidate,
        distance: Math.hypot(
          player.x - candidate.exteriorDoor.x,
          player.y - candidate.exteriorDoor.y
        )
      }))
      .filter(candidate => candidate.distance <= maxRange)
      .sort((a, b) => a.distance - b.distance)[0]
    if (!house) return false

    player.x = house.interiorSpawn.x
    player.y = house.interiorSpawn.y
    player.velocity = { x: 0, y: 0 }
    this.game.quickSave?.()
    return true
  }

  isPlayerInPostgameVillage() {
    const player = this.game.player
    if (!player || !this.postgameVillage?.unlocked) return false
    if (this.getVillageHouseInteriorAt(player.x, player.y)) return true
    const { bounds } = this.postgameVillage
    return player.x >= bounds.minX && player.x <= bounds.maxX &&
      player.y >= bounds.minY && player.y <= bounds.maxY
  }

  interactWithVillageGate(maxRange = 2) {
    const player = this.game.player
    const village = this.postgameVillage
    if (!player || !village?.unlocked || this.getVillageHouseInteriorAt(player.x, player.y)) return false
    const insideVillage = this.isPlayerInPostgameVillage()
    const gate = insideVillage ? village.villageGate : village.forestGate
    if (Math.hypot(player.x - gate.x, player.y - gate.y) > maxRange) return false
    const destination = insideVillage ? village.forestReturn : village.villageReturn
    player.x = destination.x
    player.y = destination.y
    player.velocity = { x: 0, y: 0 }
    this.game.quickSave?.()
    return true
  }

  interactWithVillageGateAt(targetX, targetY, maxRange = 2) {
    const village = this.postgameVillage
    if (!village?.unlocked) return false
    const gate = this.isPlayerInPostgameVillage() ? village.villageGate : village.forestGate
    if (Math.hypot(targetX - gate.x, targetY - gate.y) > 1.5) return false
    return this.interactWithVillageGate(maxRange)
  }

  interactWithVillageHouseDoorAt(targetX, targetY, maxRange = 2) {
    const player = this.game.player
    if (!player || !this.postgameVillage?.unlocked) return false
    const interiorHouse = this.getVillageHouseInteriorAt(player.x, player.y)
    const doors = interiorHouse
      ? [interiorHouse.exitDoor]
      : this.postgameVillage.houses.map(house => house.exteriorDoor)
    const door = doors.find(candidate => (
      Math.hypot(targetX - candidate.x, targetY - candidate.y) <= 1.5 &&
      Math.hypot(player.x - candidate.x, player.y - candidate.y) <= maxRange
    ))
    return door ? this.interactWithVillageHouse(maxRange) : false
  }

  wakeFromHomeSleep(completed = false) {
    const player = this.game.player
    if (!player?.isSleeping) return false
    player.isSleeping = false
    player.x = this.home.bed.x
    player.y = this.home.bed.y - 1.2
    player.facing = 'down'
    this.home.sleepTimer = null

    if (completed) {
      player.hearts = player.maxHearts
      if (this.game.dayNight) this.game.dayNight.currentTime = 0.25
      this.game.quickSave?.()
      this.game.showItemPopup?.('Your hearts are full. It’s morning.')
    }
    return true
  }

  standFromHomeCouch() {
    const player = this.game.player
    if (!player || (!player.isSitting && !this.isPlayerInsideHomeCouch())) return false
    const villageHouse = player.sittingVillageHouseId
      ? this.postgameVillage?.houses.find(house => house.id === player.sittingVillageHouseId)
      : null
    player.isSitting = false
    if (villageHouse) {
      const centerX = (villageHouse.interiorBounds.minX + villageHouse.interiorBounds.maxX) / 2
      player.x = centerX
      player.y = villageHouse.interiorBounds.minY + 7.4
    } else {
      player.x = this.home.interiorSpawn.x
      player.y = this.home.interiorSpawn.y
    }
    player.sittingVillageHouseId = null
    player.facing = 'down'
    player.velocity = { x: 0, y: 0 }
    return true
  }

  isPlayerInsideHomeCouch() {
    const player = this.game.player
    return player
      ? this.isInsideHomeCouchAt(player.x, player.y)
      : false
  }

  isInsideHomeCouchAt(x, y) {
    if (!this.isInsideHome(x, y)) return false
    return (
      Math.abs(x - this.home.couch.x) <= 1.5 &&
      Math.abs(y - this.home.couch.y) <= 0.65
    )
  }

  shouldConcealHomeInteriorAt(x, y) {
    const player = this.game.player
    if (player && this.isInsideHome(player.x, player.y)) return false
    const bounds = this.getCellBounds(this.grids[0], 0, 0)
    return (
      x >= bounds.minX &&
      x <= bounds.maxX &&
      y >= bounds.minY &&
      y <= bounds.maxY
    )
  }

  collidesWithHome(x, y, radius = 0.4) {
    if (this.isInsideHome(x, y)) {
      const hitsRect = (item, halfWidth, halfHeight) => (
        x + radius > item.x - halfWidth &&
        x - radius < item.x + halfWidth &&
        y + radius > item.y - halfHeight &&
        y - radius < item.y + halfHeight
      )

      if (hitsRect(this.home.exitDoor, 0.65, 0.85)) return true
      if (!this.game.player?.isSitting && hitsRect(this.home.couch, 1.5, 0.55)) return true
      if (hitsRect(this.getFireplaceInFrontOfCouch(this.home.couch), 1.12, 0.83)) return true
      if (!this.game.player?.isSleeping && hitsRect(this.home.bed, 0.9, 0.5)) return true
      if (hitsRect(this.home.table, 0.7, 0.36)) return true
      return false
    }

    const door = this.getHomeExteriorDoor()
    return (
      x + radius > door.x - 2 &&
      x - radius < door.x + 2 &&
      y + radius > door.y - 3.2 &&
      y - radius < door.y - 0.35
    )
  }

  collidesWithPostgameVillage(x, y, radius = 0.4) {
    if (!this.postgameVillage?.unlocked) return false
    const villageGate = this.isPlayerInPostgameVillage()
      ? this.postgameVillage.villageGate
      : this.postgameVillage.forestGate
    if (
      x + radius > villageGate.x - 0.5 && x - radius < villageGate.x + 0.5 &&
      y + radius > villageGate.y - 0.5 && y - radius < villageGate.y + 0.5
    ) return true
    const interiorHouse = this.getVillageHouseInteriorAt(x, y)
    if (interiorHouse) {
      const centerX = (interiorHouse.interiorBounds.minX + interiorHouse.interiorBounds.maxX) / 2
      const minY = interiorHouse.interiorBounds.minY
      const at = ([offsetX, offsetY]) => ({ x: centerX + offsetX, y: minY + offsetY })
      const hitsRect = (item, halfWidth, halfHeight) => (
        x + radius > item.x - halfWidth && x - radius < item.x + halfWidth &&
        y + radius > item.y - halfHeight && y - radius < item.y + halfHeight
      )
      if (hitsRect(interiorHouse.exitDoor, 0.65, 0.85)) return true
      // Match the complete visible furniture silhouettes, with enough depth
      // to prevent slipping through from either the front or the back.
      const couch = at(interiorHouse.furniture.couch)
      if (!this.game.player?.isSitting && hitsRect(couch, 1.55, 0.65)) return true
      if (hitsRect(this.getFireplaceInFrontOfCouch(couch), 1.12, 0.83)) return true
      if (hitsRect(at(interiorHouse.furniture.bed), 0.72, 0.72)) return true
      if (hitsRect(at(interiorHouse.furniture.table), 0.7, 0.36)) return true
      return false
    }
    const playerHomeSign = this.postgameVillage.playerHome.sign
    if (
      x + radius > playerHomeSign.x - 1.45 && x - radius < playerHomeSign.x + 1.45 &&
      y + radius > playerHomeSign.y - 0.55 && y - radius < playerHomeSign.y + 0.75
    ) return true
    return [...this.postgameVillage.houses, this.postgameVillage.playerHome].some(house => (
      x + radius > house.x - 2 &&
      x - radius < house.x + 2 &&
      y + radius > house.y - 3.2 &&
      y - radius < house.y - 0.35
    ))
  }

  drawVillageHouse(renderer, house) {
    const x = house.x
    const y = house.y
    renderer.fillRect(x - 2, y - 3.2, 4, 2.85, '#704126')
    renderer.fillRect(x - 1.75, y - 2.95, 3.5, 2.6, '#d7a96c')
    renderer.fillRect(x - 2.3, y - 3.5, 4.6, 0.55, house.color)
    renderer.fillRect(x - 1.45, y - 2.5, 0.8, 0.75, '#83c5d9')
    renderer.fillRect(x + 0.65, y - 2.5, 0.8, 0.75, '#83c5d9')
    renderer.fillRect(x - 0.55, y - 1.75, 1.1, 1.4, '#4a2d22')
    renderer.fillCircle(x + 0.3, y - 1.05, 0.08, '#ffe27a')
  }

  drawPlayerHomeSign(renderer, sign) {
    renderer.fillRect(sign.x - 1.5, sign.y - 0.62, 3, 0.95, '#4a2d1f')
    renderer.fillRect(sign.x - 1.35, sign.y - 0.48, 2.7, 0.65, '#a86836')
    renderer.fillRect(sign.x - 1.18, sign.y - 0.36, 2.36, 0.08, '#d69a55')
    renderer.fillRect(sign.x - 0.92, sign.y + 0.28, 0.22, 0.9, '#5b3825')
    renderer.fillRect(sign.x + 0.7, sign.y + 0.28, 0.22, 0.9, '#5b3825')
    renderer.drawText('BEARS HOUSE', sign.x, sign.y + 0.02, '#fff0b0', 5)
  }

  drawVillageGate(renderer, gate) {
    renderer.fillRect(gate.x - 0.5, gate.y - 0.5, 1, 1, '#7a4328')
    renderer.fillCircle(gate.x + 0.34, gate.y, 0.1, '#f2c14e')
  }

  drawVillager(renderer, villager) {
    const set = VILLAGER_SPRITE_SETS[villager.appearanceIndex]
    const sleeping = villager.interiorActivity === 'sleeping'
    const moving = !sleeping && (villager.x !== villager.targetX || villager.y !== villager.targetY)
    const frames = set.walking[villager.facing]
    const sprite = moving
      ? frames[Math.floor(villager.walkTime * 8) % frames.length]
      : set.standing[villager.facing]
    renderer.drawSprite(
      sprite,
      villager.x - 0.5,
      villager.y - 0.5,
      sleeping ? Math.PI / 2 : 0,
      1
    )
  }

  getVillagerHome(villager) {
    return this.postgameVillage?.houses.find(house => house.id === villager.homeId) || null
  }

  chooseVillagerInteriorActivity(villager, house) {
    const centerX = (house.interiorBounds.minX + house.interiorBounds.maxX) / 2
    const minY = house.interiorBounds.minY
    const at = ([offsetX, offsetY]) => ({ x: centerX + offsetX, y: minY + offsetY })
    const activities = ['walking', 'sitting', 'walking', 'sleeping']
    const activity = activities[villager.interiorActivityIndex % activities.length]
    villager.interiorActivityIndex += 1

    let target
    if (activity === 'sitting') {
      target = at(house.furniture.couch)
      target.y += 0.12
      villager.facing = 'down'
    } else if (activity === 'sleeping') {
      target = at(house.furniture.bed)
      villager.facing = 'right'
    } else {
      const floorSpots = [
        { x: centerX - 2.7, y: minY + 4.7 },
        { x: centerX + 2.7, y: minY + 4.7 },
        { x: centerX, y: minY + 7.35 },
        { x: centerX, y: minY + 4.5 }
      ]
      target = floorSpots[villager.interiorActivityIndex % floorSpots.length]
    }

    const dx = target.x - villager.x
    const dy = target.y - villager.y
    villager.targetX = target.x
    villager.targetY = target.y
    villager.interiorActivity = 'walking'
    villager.interiorDestinationActivity = activity
    villager.interiorActionTimer = activity === 'walking'
      ? 1 + Math.random() * 2
      : 2.5 + Math.random() * 3.5
    if (Math.abs(dx) > Math.abs(dy)) villager.facing = dx < 0 ? 'left' : 'right'
    else if (dy !== 0) villager.facing = dy < 0 ? 'up' : 'down'
  }

  getVillageHouseDoorTile(house) {
    return {
      x: Math.floor(house.exteriorReturn.x),
      y: Math.floor(house.exteriorReturn.y)
    }
  }

  canVillagerEnterHouse(villager, house) {
    return Boolean(villager && house && villager.homeId === house.id)
  }

  canVillagerUseVillagePath(villager, x, y) {
    const ownerId = this.villageHousePathOwners?.get(`${Math.floor(x)},${Math.floor(y)}`)
    return !ownerId || ownerId === villager?.homeId
  }

  getVillagerStepTowardHome(villager) {
    const home = this.getVillagerHome(villager)
    if (!home) return null
    const start = { x: Math.floor(villager.x), y: Math.floor(villager.y) }
    const goal = this.getVillageHouseDoorTile(home)
    if (start.x === goal.x && start.y === goal.y) return { ...goal, facing: villager.facing }

    const bounds = this.postgameVillage.bounds
    const queue = [{ ...start, first: null }]
    const visited = new Set([`${start.x},${start.y}`])
    const directions = [
      [1, 0, 'right'], [-1, 0, 'left'], [0, 1, 'down'], [0, -1, 'up']
    ]
    while (queue.length) {
      const current = queue.shift()
      for (const [dx, dy, facing] of directions) {
        const x = current.x + dx
        const y = current.y + dy
        const key = `${x},${y}`
        if (visited.has(key) || x <= bounds.minX || x >= bounds.maxX ||
            y <= bounds.minY || y >= bounds.maxY ||
            this.getTileAt(x, y) !== BLOCK_TYPES.PATH ||
            !this.canVillagerUseVillagePath(villager, x, y)) continue
        const first = current.first || { x, y, facing }
        if (x === goal.x && y === goal.y) return first
        visited.add(key)
        queue.push({ x, y, first })
      }
    }
    return null
  }

  drawBush(renderer, bush) {
    const [dark, body, highlight] = bush.colors
    const patterns = [
      [
        '...hh....',
        '..hbbh...',
        '.hbbbbbh.',
        'hbbbbbbbh',
        'dbbbbbbbd',
        '.ddddddd.'
      ],
      [
        '.h...h...',
        'hbh.hbh..',
        'bbbbbbbh.',
        'hbbbbbbbb',
        'dbbbbbbbd',
        '.ddddddd.'
      ],
      [
        '....h....',
        '..hbbbh..',
        '.hbbbbbh.',
        'bbbbbbbbb',
        'dbbbbbbbd',
        '.ddddddd.'
      ]
    ]
    const pattern = patterns[bush.kind % patterns.length]
    const pixel = 0.14
    const width = pattern[0].length * pixel
    const top = bush.y - pattern.length * pixel + 0.28
    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        const leaf = pattern[row][col]
        if (leaf === '.') continue
        const color = leaf === 'd' ? dark : leaf === 'h' ? highlight : body
        renderer.fillRect(
          bush.x - width / 2 + col * pixel,
          top + row * pixel,
          pixel,
          pixel,
          color
        )
      }
    }
  }

  drawVillageInterior(renderer, house) {
    const centerX = (house.interiorBounds.minX + house.interiorBounds.maxX) / 2
    const minY = house.interiorBounds.minY
    const at = ([offsetX, offsetY]) => ({ x: centerX + offsetX, y: minY + offsetY })
    const couch = at(house.furniture.couch)
    const bed = at(house.furniture.bed)
    const table = at(house.furniture.table)
    const rug = at(house.furniture.rug)

    renderer.fillRect(house.exitDoor.x - 0.6, house.exitDoor.y - 0.35, 1.2, 1.55, '#7c4427')
    renderer.fillCircle(house.exitDoor.x + 0.28, house.exitDoor.y + 0.42, 0.07, '#ffe27a')

    // The same furniture quality as the player's home, arranged uniquely.
    renderer.fillRect(couch.x - 1.35, couch.y - 0.55, 2.7, 0.38, '#4b293b')
    renderer.fillRect(couch.x - 1.12, couch.y - 0.12, 1.02, 0.5, house.color)
    renderer.fillRect(couch.x + 0.1, couch.y - 0.12, 1.02, 0.5, house.color)
    renderer.fillRect(couch.x - 1.5, couch.y - 0.45, 0.3, 0.82, '#4b293b')
    renderer.fillRect(couch.x + 1.2, couch.y - 0.45, 0.3, 0.82, '#4b293b')
    renderer.fillRect(couch.x - 1.35, couch.y + 0.32, 2.7, 0.24, '#693652')
    this.drawFireplace(renderer, this.getFireplaceInFrontOfCouch(couch))

    // A complete home-style bed: wooden frame, raised headboard, pillow,
    // mattress, blanket, and footboard. Its position remains layout-specific.
    renderer.fillRect(bed.x - 0.65, bed.y - 0.62, 1.3, 1.18, '#613b27')
    renderer.fillRect(bed.x - 0.65, bed.y - 0.78, 1.3, 0.24, '#4b2b1d')
    renderer.fillRect(bed.x - 0.52, bed.y - 0.5, 1.04, 0.86, '#e9d7b1')
    renderer.fillRect(bed.x - 0.42, bed.y - 0.42, 0.84, 0.25, '#fff4d8')
    renderer.fillRect(bed.x - 0.52, bed.y - 0.06, 1.04, 0.42, house.color)
    renderer.fillRect(bed.x - 0.65, bed.y + 0.46, 1.3, 0.18, '#4b2b1d')

    renderer.fillRect(table.x - 0.7, table.y - 0.36, 1.4, 0.72, '#85522f')
    renderer.fillRect(table.x - 0.52, table.y + 0.36, 0.16, 0.35, '#4e311f')
    renderer.fillRect(table.x + 0.36, table.y + 0.36, 0.16, 0.35, '#4e311f')
    renderer.fillCircle(table.x, table.y - 0.63, 0.22, '#ffe27a')

    renderer.fillRect(rug.x - 0.8, rug.y - 0.4, 1.6, 0.8, house.color)
    renderer.fillRect(rug.x - 0.5, rug.y - 0.2, 1, 0.4, '#d7a96c')
  }

  getFireplaceInFrontOfCouch(couch) {
    return { x: couch.x, y: couch.y + 1.8 }
  }

  drawFireplace(renderer, fireplace) {
    const x = fireplace.x
    const y = fireplace.y
    const flicker = Math.floor(this.fireAnimationTime * 8) % 2

    renderer.fillRect(x - 0.92, y - 0.8, 1.84, 1.5, '#4a2b24')
    renderer.fillRect(x - 0.78, y - 0.68, 1.56, 1.25, '#9a4e38')
    renderer.fillRect(x - 0.78, y - 0.12, 1.56, 0.12, '#713727')
    renderer.fillRect(x - 0.9, y - 0.72, 0.18, 0.7, '#c16b49')
    renderer.fillRect(x + 0.72, y - 0.72, 0.18, 0.7, '#6f3429')
    renderer.fillRect(x - 1.05, y - 0.88, 2.1, 0.22, '#5c382c')
    renderer.fillRect(x - 1.12, y + 0.55, 2.24, 0.28, '#63483b')
    renderer.fillRect(x - 0.92, y + 0.38, 1.84, 0.2, '#92705b')

    renderer.fillRect(x - 0.58, y - 0.02, 1.16, 0.52, '#190f12')
    renderer.fillRect(x - 0.48, y + 0.32, 0.78, 0.12, '#6b3821')
    renderer.fillRect(x - 0.28, y + 0.3, 0.76, 0.12, '#8a4b25')
    renderer.fillCircle(x, y + 0.18, 0.3, '#ef5b28')
    renderer.fillCircle(x - 0.14 + flicker * 0.12, y + 0.02, 0.22, '#ff9f2d')
    renderer.fillCircle(x + 0.08 - flicker * 0.1, y - 0.12, 0.16, '#ffd45c')
    renderer.fillRect(x - 0.08, y + 0.05, 0.16, 0.28, '#fff0a0')
  }

  getOutsideAreaIdAt(x, y) {
    const firstGrid = this.grids[0]
    if (firstGrid && y < firstGrid.originY) return firstGrid.areaId
    for (const grid of this.grids) {
      const minY = grid.originY
      const maxY = grid.originY + grid.rows * grid.cellSize
      if (y >= minY && y <= maxY) return grid.areaId
    }
    return this.grids.at(-1)?.areaId || 'forest'
  }

  getActiveRenderAreaId() {
    const player = this.game.player
    if (!player) return null
    if (this.isPlayerInPostgameVillage()) return 'village'
    const cell = this.getGridCellForPosition(player.x, player.y)
    return cell ? this.grids[cell.gridIndex]?.areaId || null : null
  }

  isPositionInActiveLevel(x, y) {
    const activeAreaId = this.getActiveRenderAreaId()
    if (!activeAreaId || activeAreaId === 'village') return true
    const cell = this.getGridCellForPosition(x, y)
    return !cell || this.grids[cell.gridIndex]?.areaId === activeAreaId
  }

  isNextLevelEntrancePreview(x, y) {
    const activeAreaId = this.getActiveRenderAreaId()
    const activeIndex = this.grids.findIndex(grid => grid.areaId === activeAreaId)
    if (activeIndex < 0 || activeIndex >= this.grids.length - 1) return false

    const activeGrid = this.grids[activeIndex]
    const nextGrid = this.grids[activeIndex + 1]
    const exitCol = Array.from({ length: activeGrid.cols }, (_, col) => col)
      .find(col => activeGrid.getCell(col, activeGrid.rows - 1)?.walls.s === false)
    if (exitCol === undefined) return false

    const doorwayX = activeGrid.getCellCenter(exitCol, activeGrid.rows - 1).x
    return x >= doorwayX - 2 && x <= doorwayX + 2 &&
      y >= nextGrid.originY && y <= nextGrid.originY + 2
  }

  getOutsideGroundColor(areaId, x, y) {
    const alternate = ((x * 17 + y * 31) & 7) === 0
    const colors = {
      forest: alternate ? '#326f38' : '#285f31',
      graveyard: alternate ? '#514b55' : '#403c46',
      caves: alternate ? '#334b55' : '#293c45',
      emberkeep: alternate ? '#58332e' : '#452824',
      purpledooms: alternate ? '#30213f' : '#24182f'
    }
    return colors[areaId] || colors.forest
  }

  drawOutsideBoundary(renderer, areaId, x, y) {
    if (areaId === 'forest') {
      renderer.drawSprite(LARGE_TREE_SPRITE, x, y, 0, 1)
      return
    }
    if (areaId === 'graveyard') {
      renderer.drawSprite(GRAVESTONE_SPRITE, x, y, 0, 1)
      return
    }
    if (areaId === 'caves') {
      renderer.fillCircle(x + 0.5, y + 0.58, 0.48, '#25343c')
      renderer.fillCircle(x + 0.25, y + 0.35, 0.34, '#57717a')
      renderer.fillCircle(x + 0.72, y + 0.28, 0.3, '#405d68')
      return
    }
    if (areaId === 'emberkeep') {
      renderer.fillRect(x + 0.05, y + 0.12, 0.9, 0.88, '#4b211f')
      renderer.fillRect(x + 0.05, y + 0.12, 0.22, 0.25, '#8d3b2e')
      renderer.fillRect(x + 0.39, y + 0.12, 0.22, 0.25, '#8d3b2e')
      renderer.fillRect(x + 0.73, y + 0.12, 0.22, 0.25, '#8d3b2e')
      renderer.fillCircle(x + 0.5, y + 0.55, 0.13, '#ed7b32')
      return
    }
    renderer.fillRect(x + 0.43, y + 0.2, 0.14, 0.8, '#3c1857')
    renderer.fillRect(x + 0.28, y + 0.42, 0.18, 0.58, '#7135a0')
    renderer.fillRect(x + 0.57, y + 0.34, 0.2, 0.66, '#a05ac5')
    renderer.fillCircle(x + 0.67, y + 0.31, 0.1, '#d9a5ed')
  }

  // --- Rendering ---

  render(renderer, pass = 'all', playerY = null) {
    const bounds = renderer.getVisibleBounds()
    const splitByPlayer = playerY !== null && pass !== 'all'
    const activeVillageHouse = this.getVillageHouseInteriorAt(
      this.game.player?.x,
      this.game.player?.y
    )
    const activeAreaId = this.getActiveRenderAreaId()
    const isForeignLevel = (x, y) => (
      !this.isPositionInActiveLevel(x, y) &&
      !this.isNextLevelEntrancePreview(x, y)
    )
    const outsideActiveInterior = (x, y) => {
      if (!activeVillageHouse) return false
      const interior = activeVillageHouse.interiorBounds
      return x < interior.minX || x > interior.maxX ||
        y < interior.minY || y > interior.maxY
    }

    if (pass === 'all' || pass === 'below') {
      for (let x = bounds.minX; x <= bounds.maxX; x++) {
        for (let y = bounds.minY; y <= bounds.maxY; y++) {
          if (outsideActiveInterior(x, y)) continue
          const concealedHome = this.shouldConcealHomeInteriorAt(x, y)
          const foreignLevel = isForeignLevel(x, y)
          if (!foreignLevel && !concealedHome && this.getTileAt(x, y) !== BLOCK_TYPES.AIR) continue
          const areaId = activeAreaId && activeAreaId !== 'village'
            ? activeAreaId
            : this.getOutsideAreaIdAt(x, y)
          renderer.drawTile(x, y, this.getOutsideGroundColor(areaId, x, y))
        }
      }

      for (let x = bounds.minX; x <= bounds.maxX; x++) {
        for (let y = bounds.minY; y <= bounds.maxY; y++) {
          if (outsideActiveInterior(x, y)) continue
          if (isForeignLevel(x, y)) continue
          if (this.shouldConcealHomeInteriorAt(x, y)) continue
          const tile = this.getTileAt(x, y)
          if (tile === BLOCK_TYPES.KEY) continue
          if (!this.isTileRevealed(x, y)) continue
          if (tile !== BLOCK_TYPES.AIR && !isBlockRaised(tile)) {
            renderer.drawTile(x, y, getBlockColor(tile))
          }
        }
      }

      for (let x = bounds.minX; x <= bounds.maxX; x++) {
        for (let y = bounds.minY; y <= bounds.maxY; y++) {
          if (outsideActiveInterior(x, y)) continue
          if (isForeignLevel(x, y)) continue
          if (this.shouldConcealHomeInteriorAt(x, y)) continue
          if (!this.isTileRevealed(x, y)) continue
          if (this.getTileAt(x, y) === BLOCK_TYPES.KEY) {
            renderer.drawSprite(KEY_SPRITE, x, y, 0, 1)
          }
        }
      }
    }

    const shouldDrawAtY = (y) => {
      if (!splitByPlayer) return true
      if (pass === 'below') return y <= playerY
      return y > playerY
    }

    for (let x = bounds.minX; x <= bounds.maxX; x++) {
      for (let y = bounds.minY; y <= bounds.maxY; y++) {
        if (outsideActiveInterior(x, y)) continue
        if (!shouldDrawAtY(y)) continue
        const concealedHome = this.shouldConcealHomeInteriorAt(x, y)
        const foreignLevel = isForeignLevel(x, y)
        if (!foreignLevel && !concealedHome && this.getTileAt(x, y) !== BLOCK_TYPES.AIR) continue
        if (Math.abs(x + y) % 2 === 0) {
          const areaId = activeAreaId && activeAreaId !== 'village'
            ? activeAreaId
            : this.getOutsideAreaIdAt(x, y)
          this.drawOutsideBoundary(renderer, areaId, x, y)
        }
      }
    }

    for (let x = bounds.minX; x <= bounds.maxX; x++) {
      for (let y = bounds.minY; y <= bounds.maxY; y++) {
        if (outsideActiveInterior(x, y)) continue
        if (isForeignLevel(x, y)) continue
        if (!shouldDrawAtY(y)) continue
        if (this.shouldConcealHomeInteriorAt(x, y)) continue
        if (!this.isTileRevealed(x, y)) continue
        const tile = this.getTileAt(x, y)
        if (tile !== BLOCK_TYPES.AIR && isBlockRaised(tile)) {
          if (tile === BLOCK_TYPES.TREE) {
            renderer.drawSprite(LARGE_TREE_SPRITE, x, y, 0, 1)
          } else {
            renderer.drawRaisedBlock(x, y, getBlockColor(tile), tile)
          }
        }
      }
    }

    for (const deco of this.decorations) {
      if (deco.x >= bounds.minX && deco.x <= bounds.maxX &&
          deco.y >= bounds.minY && deco.y <= bounds.maxY &&
          this.isPositionInActiveLevel(deco.x, deco.y) &&
          !this.shouldConcealHomeInteriorAt(deco.x, deco.y) &&
          this.isTileRevealed(deco.x, deco.y) &&
          shouldDrawAtY(deco.y)) {
        if (deco.type === 'tree' || deco.type === 'dark_tree') {
          renderer.drawSprite(LARGE_TREE_SPRITE, deco.x, deco.y, 0, 1)
        } else if (deco.type === 'gravestone') {
          renderer.drawSprite(GRAVESTONE_SPRITE, deco.x, deco.y, 0, 1)
        }
      }
    }

    if (!activeVillageHouse) {
      for (const bush of this.bushes) {
        if (bush.x >= bounds.minX && bush.x <= bounds.maxX &&
            bush.y >= bounds.minY && bush.y <= bounds.maxY &&
            this.isPositionInActiveLevel(bush.x, bush.y) &&
            this.isTileRevealed(bush.x, bush.y) &&
            shouldDrawAtY(bush.y)) {
          this.drawBush(renderer, bush)
        }
      }
    }

    // Area map pickups
    for (const pickup of this.mapPickups) {
      const areaMap = this.getAreaMapState(pickup.areaId)
      if (areaMap?.mapFound) continue
      if (pickup.x >= bounds.minX && pickup.x <= bounds.maxX &&
          pickup.y >= bounds.minY && pickup.y <= bounds.maxY &&
          this.isPositionInActiveLevel(pickup.x, pickup.y) &&
          this.isTileRevealed(pickup.x, pickup.y) &&
          shouldDrawAtY(pickup.y)) {
        renderer.drawSprite(MAP_SCROLL_SPRITE, pickup.x - 0.5, pickup.y - 0.5)
      }
    }

    // Sword pickup
    if (this.swordPickup && !this.swordPickup.collected) {
      const s = this.swordPickup
      if (s.x >= bounds.minX && s.x <= bounds.maxX &&
          s.y >= bounds.minY && s.y <= bounds.maxY &&
          this.isTileRevealed(s.x, s.y) &&
          shouldDrawAtY(s.y)) {
        renderer.drawSprite(SWORD_SPRITE, s.x - 0.5, s.y - 0.5, 0, 1)
      }
    }

    const home = this.home
    if (!activeVillageHouse && home && !this.postgameVillage?.unlocked && shouldDrawAtY(home.exteriorDoor.y)) {
      const x = home.exteriorDoor.x
      const y = home.exteriorDoor.y
      renderer.fillRect(x - 2, y - 3.2, 4, 2.85, '#6f3d25')
      renderer.fillRect(x - 2.3, y - 3.5, 4.6, 0.55, '#b44c35')
      renderer.fillRect(x - 1.55, y - 2.45, 0.8, 0.75, '#79b9d2')
      renderer.fillRect(x + 0.75, y - 2.45, 0.8, 0.75, '#79b9d2')
      renderer.fillRect(x - 0.55, y - 1.75, 1.1, 1.4, '#38251d')
      renderer.fillCircle(x + 0.3, y - 1.05, 0.08, '#ffe27a')
    }

    if (this.postgameVillage?.unlocked) {
      const insideVillage = this.isPlayerInPostgameVillage()
      if (!activeVillageHouse && insideVillage) {
        for (const house of this.postgameVillage.houses) {
          if (shouldDrawAtY(house.y)) this.drawVillageHouse(renderer, house)
        }
        const playerHome = this.postgameVillage.playerHome
        if (shouldDrawAtY(playerHome.y)) {
          this.drawVillageHouse(renderer, playerHome)
          this.drawPlayerHomeSign(renderer, playerHome.sign)
        }
        for (const villager of this.postgameVillage.villagers) {
          if (!villager.insideHomeId && shouldDrawAtY(villager.y)) this.drawVillager(renderer, villager)
        }
        if (shouldDrawAtY(this.postgameVillage.villageGate.y)) {
          this.drawVillageGate(renderer, this.postgameVillage.villageGate)
        }
      } else if (!activeVillageHouse && shouldDrawAtY(this.postgameVillage.forestGate.y)) {
        this.drawVillageGate(renderer, this.postgameVillage.forestGate)
      }
      if (activeVillageHouse && (pass === 'all' || pass === 'below')) {
        this.drawVillageInterior(renderer, activeVillageHouse)
        const resident = this.postgameVillage.villagers.find(villager => (
          villager.insideHomeId === activeVillageHouse.id
        ))
        if (resident) this.drawVillager(renderer, resident)
      }
    }

    if (home &&
        this.isInsideHome(this.game.player?.x, this.game.player?.y) &&
        (pass === 'all' || pass === 'below')) {
      // Orange exit door set into the top wooden wall.
      renderer.fillRect(home.exitDoor.x - 0.65, home.exitDoor.y - 0.35, 1.3, 1.7, '#7c321c')
      renderer.fillRect(home.exitDoor.x - 0.48, home.exitDoor.y - 0.18, 0.96, 1.42, '#e86f2b')
      renderer.fillRect(home.exitDoor.x - 0.34, home.exitDoor.y - 0.04, 0.68, 0.22, '#ffad43')
      renderer.fillCircle(home.exitDoor.x + 0.28, home.exitDoor.y + 0.62, 0.08, '#ffe27a')
      if (!home.doorUnlocked) {
        renderer.fillRect(home.exitDoor.x - 0.12, home.exitDoor.y + 0.38, 0.24, 0.34, '#e7ad3d')
      }

      if (!home.hasKey) {
        // A little house sign above the Home Key on the right.
        renderer.fillRect(home.key.x - 0.5, home.key.y - 1.15, 1, 0.72, '#ead7ab')
        renderer.fillRect(home.key.x - 0.3, home.key.y - 1.02, 0.6, 0.42, '#b44c35')
        renderer.fillRect(home.key.x - 0.18, home.key.y - 0.86, 0.36, 0.26, '#f4d48b')
        renderer.fillRect(home.key.x - 0.06, home.key.y - 0.72, 0.12, 0.12, '#6f3d25')
        renderer.fillCircle(home.key.x - 0.2, home.key.y, 0.2, '#ffe27a')
        renderer.fillRect(home.key.x, home.key.y - 0.06, 0.6, 0.12, '#e7ad3d')
        renderer.fillRect(home.key.x + 0.42, home.key.y - 0.06, 0.12, 0.3, '#e7ad3d')
      }

      // Couch with arms, cushions, and wooden feet.
      renderer.fillRect(home.couch.x - 1.35, home.couch.y - 0.55, 2.7, 0.38, '#4b293b')
      renderer.fillRect(home.couch.x - 1.12, home.couch.y - 0.12, 1.02, 0.5, '#b85e79')
      renderer.fillRect(home.couch.x + 0.1, home.couch.y - 0.12, 1.02, 0.5, '#b85e79')
      renderer.fillRect(home.couch.x - 1.5, home.couch.y - 0.45, 0.3, 0.82, '#4b293b')
      renderer.fillRect(home.couch.x + 1.2, home.couch.y - 0.45, 0.3, 0.82, '#4b293b')
      renderer.fillRect(home.couch.x - 1.35, home.couch.y + 0.32, 2.7, 0.24, '#693652')
      renderer.fillRect(home.couch.x - 1.15, home.couch.y + 0.55, 0.18, 0.22, '#40291d')
      renderer.fillRect(home.couch.x + 0.97, home.couch.y + 0.55, 0.18, 0.22, '#40291d')
      this.drawFireplace(renderer, this.getFireplaceInFrontOfCouch(home.couch))

      // Bed, rug, table, lamp, and wall shelf.
      renderer.fillRect(home.bed.x - 0.9, home.bed.y - 0.5, 1.8, 1.0, '#613b27')
      renderer.fillRect(home.bed.x - 0.7, home.bed.y - 0.42, 1.4, 0.72, '#e9d7b1')
      renderer.fillRect(home.bed.x - 0.7, home.bed.y, 1.4, 0.3, '#7191bd')
      renderer.fillRect(home.table.x - 0.7, home.table.y - 0.36, 1.4, 0.72, '#85522f')
      renderer.fillRect(home.table.x - 0.52, home.table.y + 0.36, 0.16, 0.35, '#4e311f')
      renderer.fillRect(home.table.x + 0.36, home.table.y + 0.36, 0.16, 0.35, '#4e311f')
      renderer.fillCircle(home.table.x, home.table.y - 0.63, 0.22, '#ffe27a')
      renderer.fillRect(home.rug.x - 0.8, home.rug.y - 0.4, 1.6, 0.8, '#8b3d34')
      renderer.fillRect(home.rug.x - 0.5, home.rug.y - 0.2, 1.0, 0.4, '#c7674d')
      renderer.fillRect(home.interiorSpawn.x - 2.4, home.interiorSpawn.y - 2.25, 1.3, 0.16, '#52331f')
      renderer.fillRect(home.interiorSpawn.x - 2.25, home.interiorSpawn.y - 2.5, 0.25, 0.25, '#79c9e8')
      renderer.fillRect(home.interiorSpawn.x - 1.75, home.interiorSpawn.y - 2.52, 0.3, 0.27, '#e7ad3d')
    }
  }

  update(delta) {
    this.fireAnimationTime += delta
    if (this.game.player) {
      this.markVisitedFromPosition(this.game.player.x, this.game.player.y)
      const exit = this.getLevelAdvanceExit(this.getActiveRenderAreaId())
      if (!exit || Math.hypot(this.game.player.x - exit.x, this.game.player.y - exit.y) > 2) {
        this.exitLockNoticeAreaId = null
      }
      if (
        !this.home.hasKey &&
        this.isInsideHome(this.game.player.x, this.game.player.y) &&
        Math.hypot(
          this.game.player.x - this.home.key.x,
          this.game.player.y - this.home.key.y
        ) <= 1
      ) {
        this.collectHomeKey()
      }
    }

    if (this.home.sleepTimer !== null && this.game.player?.isSleeping) {
      this.home.sleepTimer += delta
      if (this.home.sleepTimer >= this.home.sleepDuration) {
        this.wakeFromHomeSleep(true)
      }
    }

    if (this.postgameVillage?.unlocked) {
      const bounds = this.postgameVillage.bounds
      for (const villager of this.postgameVillage.villagers) {
        if (villager.insideHomeId) {
          villager.homeTimer -= delta
          const home = this.getVillagerHome(villager)
          if (!this.canVillagerEnterHouse(villager, home)) continue
          if (villager.homeTimer > 0) {
            const dx = villager.targetX - villager.x
            const dy = villager.targetY - villager.y
            const distance = Math.hypot(dx, dy)
            if (distance > 0.01) {
              const step = Math.min(distance, villager.speed * 0.65 * delta)
              villager.x += dx / distance * step
              villager.y += dy / distance * step
              villager.walkTime += delta
              continue
            }
            villager.x = villager.targetX
            villager.y = villager.targetY
            if (villager.interiorActivity === 'walking') {
              villager.interiorActivity = villager.interiorDestinationActivity
              villager.walkTime = 0
            }
            villager.interiorActionTimer -= delta
            if (villager.interiorActionTimer <= 0) {
              this.chooseVillagerInteriorActivity(villager, home)
            }
            continue
          }
          const doorTile = this.getVillageHouseDoorTile(home)
          villager.x = doorTile.x + 0.5
          villager.y = doorTile.y + 0.5
          villager.targetX = villager.x
          villager.targetY = villager.y
          villager.previousX = villager.x
          villager.previousY = villager.y
          villager.insideHomeId = null
          villager.headingHome = false
          villager.interiorActivity = 'walking'
          villager.facing = 'down'
          villager.restTimer = 0.5 + Math.random()
          continue
        }
        const dx = villager.targetX - villager.x
        const dy = villager.targetY - villager.y
        const distance = Math.hypot(dx, dy)
        if (distance > 0.01) {
          const step = Math.min(distance, villager.speed * delta)
          villager.x += dx / distance * step
          villager.y += dy / distance * step
          villager.walkTime += delta
          continue
        }
        villager.x = villager.targetX
        villager.y = villager.targetY
        villager.restTimer -= delta
        if (villager.restTimer > 0) continue
        const tileX = Math.floor(villager.x)
        const tileY = Math.floor(villager.y)
        const home = this.getVillagerHome(villager)
        const homeDoor = home && this.getVillageHouseDoorTile(home)
        if (villager.headingHome && homeDoor && tileX === homeDoor.x && tileY === homeDoor.y &&
            this.canVillagerEnterHouse(villager, home)) {
          villager.insideHomeId = home.id
          villager.homeTimer = 18 + Math.random() * 12
          villager.x = home.interiorSpawn.x
          villager.y = home.interiorSpawn.y
          villager.targetX = villager.x
          villager.targetY = villager.y
          villager.facing = 'down'
          this.chooseVillagerInteriorActivity(villager, home)
          continue
        }
        if (!villager.headingHome && Math.random() < 0.08) villager.headingHome = true
        const homeStep = villager.headingHome ? this.getVillagerStepTowardHome(villager) : null
        const choices = [
          [tileX + 1, tileY, 'right'], [tileX - 1, tileY, 'left'],
          [tileX, tileY + 1, 'down'], [tileX, tileY - 1, 'up']
        ].filter(([x, y]) => (
          x > bounds.minX && x < bounds.maxX &&
          y > bounds.minY && y < bounds.maxY &&
          this.getTileAt(x, y) === BLOCK_TYPES.PATH &&
          this.canVillagerUseVillagePath(villager, x, y) &&
          !(x + 0.5 === villager.previousX && y + 0.5 === villager.previousY)
        ))
        if (!homeStep && choices.length === 0) {
          villager.restTimer = 0.5
          continue
        }
        const [nextX, nextY, facing] = homeStep
          ? [homeStep.x, homeStep.y, homeStep.facing]
          : choices[Math.floor(Math.random() * choices.length)]
        villager.previousX = villager.x
        villager.previousY = villager.y
        villager.targetX = nextX + 0.5
        villager.targetY = nextY + 0.5
        villager.facing = facing
        villager.restTimer = 0.2 + Math.random() * 0.8
      }
    }

    for (const pickup of this.mapPickups) {
      const areaMap = this.getAreaMapState(pickup.areaId)
      if (areaMap?.mapFound || !this.game.player) continue

      const dx = this.game.player.x - pickup.x
      const dy = this.game.player.y - pickup.y
      if (Math.sqrt(dx * dx + dy * dy) >= 1) continue

      this.discoverAreaMap(pickup.areaId)
      const zone = this.zones.find(candidate => candidate.name === pickup.areaId)
      this.game.showItemPopup(`You found the ${zone?.displayName || pickup.areaId} Map!`, MAP_SCROLL_SPRITE)
      break
    }

    // Check sword pickup
    if (this.swordPickup && !this.swordPickup.collected && this.game.player) {
      const p = this.game.player
      const dx = p.x - this.swordPickup.x
      const dy = p.y - this.swordPickup.y
      if (Math.sqrt(dx * dx + dy * dy) < 1) {
        this.swordPickup.collected = true
        p.equipWeapon({
          type: 'sword',
          id: 'wood_sword',
          name: 'Wood Sword',
          damage: 10
        })
        this.game.showItemPopup('You found a Sword!')
      }
    }
  }

  serialize() {
    const areas = {}
    for (const grid of this.grids) {
      const areaMap = this.getAreaMapState(grid.areaId)
      areas[grid.areaId] = {
        mapFound: areaMap?.mapFound === true,
        exploredCells: Array.from(areaMap?.exploredCells || []).sort()
      }
    }

    return {
      modifiedTiles: Array.from(this.modifiedTiles.entries()),
      swordPickup: this.swordPickup,
      homeHasKey: this.home.hasKey,
      homeDoorUnlocked: this.home.doorUnlocked,
      postgameVillageUnlocked: this.postgameVillage?.unlocked === true,
      darkKingRetryHealth: this.darkKingRetryHealth,
      minimap: {
        version: 1,
        areas
      }
    }
  }

  deserialize(data) {
    this.resetAreaMaps()

    if (Array.isArray(data?.modifiedTiles)) {
      const validTileTypes = new Set(Object.values(BLOCK_TYPES))
      this.modifiedTiles = new Map(data.modifiedTiles.filter(entry => (
        Array.isArray(entry) &&
        entry.length === 2 &&
        typeof entry[0] === 'string' &&
        /^-?\d+,-?\d+$/.test(entry[0]) &&
        validTileTypes.has(entry[1])
      )))
    }
    if (
      data?.swordPickup &&
      Number.isFinite(data.swordPickup.x) &&
      Number.isFinite(data.swordPickup.y)
    ) {
      this.swordPickup = data.swordPickup
    }
    this.home.hasKey = data?.homeHasKey === true
    this.home.doorUnlocked = data?.homeDoorUnlocked !== false
    this.postgameVillage.unlocked = data?.postgameVillageUnlocked === true
    this.darkKingRetryHealth = Number.isFinite(data?.darkKingRetryHealth) &&
      data.darkKingRetryHealth > 0
      ? data.darkKingRetryHealth
      : null

    const savedAreas = data?.minimap?.areas
    if (savedAreas && typeof savedAreas === 'object' && !Array.isArray(savedAreas)) {
      for (const grid of this.grids) {
        const savedArea = savedAreas[grid.areaId]
        const areaMap = this.getAreaMapState(grid.areaId)
        if (!savedArea || !areaMap) continue

        areaMap.mapFound = savedArea.mapFound === true
        this.restoreExploredCells(grid, savedArea.exploredCells)
      }
    } else if (Array.isArray(data?.visitedCells)) {
      // Legacy saves stored exploration by mutable grid index.
      for (const entry of data.visitedCells) {
        if (!Array.isArray(entry) || entry.length < 2) continue
        const grid = this.grids[Number(entry[0])]
        if (!grid) continue
        this.restoreExploredCells(grid, entry[1])
      }

      // If a legacy save already explored a map room, treat its map as collected.
      for (const pickup of this.mapPickups) {
        const cell = this.getGridCellForPosition(pickup.x, pickup.y)
        const areaMap = this.getAreaMapState(pickup.areaId)
        if (!cell || !areaMap) continue
        if (areaMap.exploredCells.has(`${cell.col},${cell.row}`)) {
          areaMap.mapFound = true
        }
      }
    }

    this.rebuildRevealedTiles()
  }

  restoreExploredCells(grid, exploredCells) {
    const areaMap = this.getAreaMapState(grid.areaId)
    if (!areaMap || !Array.isArray(exploredCells)) return

    for (const key of exploredCells) {
      if (typeof key !== 'string') continue
      const parts = key.split(',')
      if (parts.length !== 2) continue

      const col = Number(parts[0])
      const row = Number(parts[1])
      if (!Number.isInteger(col) || !Number.isInteger(row)) continue

      const cell = grid.getCell(col, row)
      if (!cell?.visible) continue
      areaMap.exploredCells.add(`${col},${row}`)
    }
  }
}
